/**
 * Spirify - Multilingual Translations
 * Supported Languages: French, English, Italian, Spanish, Portuguese, Arabic
 */

export const translations = {
  fr: {
    // Navigation
    nav: {
      audience: "À qui s'adresse",
      howItWorks: "Comment ça marche",
      paths: "Parcours",
      faq: "FAQ",
      pricing: "Tarifs",
      startFree: "Commencer gratuitement",
      myDashboard: "Mon Dashboard",
      back: "Retour"
    },
    
    // Hero Section
    hero: {
      badge: "Nouvelle façon d'apprendre les religions",
      title: "Découvrez les religions en",
      titleHighlight: "5 minutes par jour",
      description: "Spirify est une plateforme ludique et respectueuse pour découvrir les religions, partager l'apprentissage en famille et pratiquer des langues à travers des contenus culturels, sans prosélytisme.",
      subtitle: "Spirify est une plateforme ludique et respectueuse pour découvrir les religions, partager l'apprentissage en famille et pratiquer des langues à travers des contenus culturels, sans prosélytisme.",
      feature1: "<strong class='text-purple-700'>Micro-leçons</strong> de 5 à 10 minutes",
      feature2: "<strong class='text-purple-700'>5+ religions</strong> et spiritualités",
      feature3: "<strong class='text-purple-700'>Approche comparative</strong> et respectueuse",
      feature4: "<strong class='text-purple-700'>Gamification</strong> (XP, niveaux, séries)",
      cta: "Commencer gratuitement",
      ctaSecondary: "Voir comment ça marche",
      noCommitment: "Aucun engagement, accès immédiat à la version découverte."
    },

    // Audience Section
    audience: {
      title: "À qui s'adresse Spirify ?",
      subtitle: "Spirify s'adresse à toutes les personnes qui veulent comprendre les religions sans entrer dans des débats théologiques complexes. Pas besoin d'être croyant ni spécialiste : il suffit d'être curieux.",
      card1Title: "Curieux & chercheurs de sens",
      card1Text: "Vous voulez mieux comprendre les religions dont on parle aux infos, en famille ou dans la société, sans avoir le temps de lire des centaines de pages.",
      card2Title: "Croyants qui veulent approfondir",
      card2Text: "Vous pratiquez déjà une religion mais vous souhaitez mieux comprendre son histoire, ses textes, ses pratiques – et découvrir comment les autres traditions fonctionnent.",
      card3Title: "Étudiants, éducateurs & médiateurs",
      card3Text: "Vous travaillez avec des jeunes, des publics variés ou en contexte interculturel, et vous avez besoin d'un outil simple, neutre et pédagogique pour parler de religion."
    },

    // How It Works
    howItWorks: {
      title: "Comment ça marche ?",
      subtitle: "L'expérience est pensée comme un jeu éducatif : vous choisissez une religion ou un parcours transversal, puis vous progressez en débloquant des niveaux, des leçons et des badges.",
      step1Title: "Choisissez votre parcours",
      step1Text: "Islam, christianisme, judaïsme ou \"Spiritualité & dialogue\" : sélectionnez le parcours qui vous intéresse le plus, ou combinez-les.",
      step2Title: "Suivez des micro-leçons quotidiennes",
      step2Text: "Chaque leçon dure entre 5 et 10 minutes, avec un mélange de textes simples, de quiz, de QCM et d'exemples concrets.",
      step3Title: "Progressez et débloquez des badges",
      step3Text: "Vous gagnez des points d'expérience (XP), vous débloquez de nouveaux chapitres et vous suivez votre progression jour après jour.",
      videoBadge: "Voir en action",
      videoTitle: "Découvrez Spirify en 2 minutes",
      videoSubtitle: "Parcourez une leçon complète et voyez comment l'apprentissage devient un jeu captivant",
      benefit1: "5 min par jour",
      benefit1Sub: "Apprenez à votre rythme, sans pression",
      benefit2: "Gamification",
      benefit2Sub: "XP, badges et classements motivants",
      benefit3: "Progression claire",
      benefit3Sub: "Suivez vos progrès en temps réel",
      demoTitle: "Découvrez notre plateforme interactive",
      demoSubtitle: "Apprenez à votre rythme avec des leçons gamifiées, des quiz interactifs et suivez votre progression en temps réel.",
      startFree: "Commencer gratuitement"
    },

    // Paths
    paths: {
      title: "Des parcours structurés pour chaque religion",
      subtitle: "Chaque tradition est présentée à travers des modules clairs : bases, textes, rites, fêtes, histoire et enjeux contemporains.",
      badge: "Parcours",
      islam: "Islam",
      islamDesc: "Bases de la foi, Coran et sourates, prière (salat), jeûne, fêtes, histoire et diversité des pratiques musulmanes.",
      islamItem1: "Bases de la foi et des piliers",
      islamItem2: "Structure du Coran et sourates",
      islamItem3: "Prière, jeûne, fêtes",
      islamItem4: "Histoire et diversité contemporaine",
      christianity: "Christianisme",
      christianityDesc: "Bible et Évangiles, principales branches, prière, sacrements, fêtes chrétiennes, histoire et enjeux actuels.",
      christianityItem1: "Bible, Nouveau Testament, Évangiles",
      christianityItem2: "Branches et traditions",
      christianityItem3: "Prière, sacrements, fêtes",
      christianityItem4: "Histoire et monde contemporain",
      judaism: "Judaïsme",
      judaismDesc: "Torah, Tanakh, fêtes juives, Shabbat, pratiques communautaires, histoire du peuple juif.",
      judaismItem1: "Textes : Torah et Tanakh",
      judaismItem2: "Shabbat et vie communautaire",
      judaismItem3: "Fêtes et calendrier",
      judaismItem4: "Grandes étapes historiques",
      buddhism: "Bouddhisme",
      buddhismDesc: "Les 4 Nobles Vérités, le Sentier Octuple, méditation, karma, Nirvana et traditions bouddhistes.",
      buddhismItem1: "Les 4 Nobles Vérités",
      buddhismItem2: "Le Sentier Octuple",
      buddhismItem3: "Méditation et pratique",
      buddhismItem4: "Écoles et traditions",
      hinduism: "Hindouisme",
      hinduismDesc: "Vedas, Upanishads, karma, dharma, yoga, divinités et pratiques spirituelles hindoues.",
      hinduismItem1: "Textes sacrés et Vedas",
      hinduismItem2: "Karma, dharma et moksha",
      hinduismItem3: "Yoga et méditation",
      hinduismItem4: "Divinités et rituels",
      taoism: "Taoïsme",
      taoismDesc: "Le Tao, Wu Wei, Yin-Yang, Zhuangzi, harmonie avec la nature et sagesse orientale.",
      taoismItem1: "Le Tao Te Ching",
      taoismItem2: "Wu Wei et non-agir",
      taoismItem3: "Yin et Yang",
      taoismItem4: "Sagesse et harmonie",
      transversal: "Transversal",
      transversalDesc: "Parcours comparatifs qui relient les traditions : non-dualité, souffrance, action juste.",
      transversalItem1: "Non-dualité & illusion",
      transversalItem2: "Souffrance & libération",
      transversalItem3: "Action juste",
      transversalItem4: "Ponts entre traditions",
      spirituality: "Spiritualité & dialogue",
      spiritualityDesc: "Valeurs communes, éthique, compassion, justice, intériorité, et modules comparatifs.",
      spiritualityItem1: "Valeurs et éthique",
      spiritualityItem2: "Prière et intériorité",
      spiritualityItem3: "Comparaisons des pratiques",
      spiritualityItem4: "Dialogue et compréhension mutuelle",
      newBadge: "Nouveau",
      approach: "Chaque parcours est conçu avec une approche factuelle et respectueuse, sans jugement ni prise de position doctrinale."
    },

    // Features
    features: {
      title: "Une pédagogie simple et gamifiée",
      subtitle: "L'objectif est de rendre l'apprentissage des religions aussi fluide et motivant que l'apprentissage d'une langue.",
      microLessons: "Micro-leçons",
      microLessonsText: "Des leçons très courtes pour progresser même avec un emploi du temps chargé.",
      quizzes: "Quiz interactifs",
      quizzesText: "QCM, vrai/faux, associations de concepts pour vérifier la compréhension.",
      comparative: "Approche comparative",
      comparativeText: "Des modules qui montrent les points communs et différences entre islam, christianisme et judaïsme.",
      gamification: "Gamification",
      gamificationText: "Points d'expérience, niveaux, séries de jours consécutifs, badges thématiques.",
      progress: "Suivi de progression",
      progressText: "Tableau de bord simple avec progression par parcours, nombre de leçons terminées.",
      revision: "Mode \"révision\"",
      revisionText: "Possibilité de rejouer rapidement les quiz pour réviser les notions clés."
    },

    // FAQ
    faq: {
      title: "Foire aux questions",
      subtitle: "Quelques réponses aux questions les plus fréquentes sur Spirify.",
      q1: "Est-ce que Spirify fait la promotion d'une religion ?",
      a1: "Non. Spirify est une plateforme éducative. Elle présente les religions étudiées de manière factuelle et respectueuse, sans prosélytisme ni jugement.",
      q2: "Dois-je être croyant pour utiliser Spirify ?",
      a2: "Non. La plateforme s'adresse aussi bien aux croyants qu'aux personnes non croyantes, curieuses ou en recherche de compréhension.",
      q3: "Qui crée le contenu ?",
      a3: "Le contenu est élaboré à partir de sources reconnues et peut être relu par des personnes formées aux études religieuses pour assurer une présentation équilibrée.",
      q4: "Toutes les religions sont-elles traitées à égalité ?",
      a4: "Oui. Chaque religion a son propre parcours structuré de la même manière. Nous proposons actuellement 7 parcours : Islam, Christianisme, Judaïsme, Bouddhisme, Hindouisme, Taoïsme et Spiritualité & Dialogue.",
      q5: "Ajoutez-vous régulièrement du nouveau contenu ?",
      a5: "Oui ! Nous enrichissons continuellement notre plateforme. Récemment, nous avons ajouté le Bouddhisme, l'Hindouisme et le Taoïsme avec 20 leçons chacun, incluant des niveaux Expert. D'autres religions et traditions spirituelles pourront être ajoutées selon la demande.",
      q6: "Comment être informé des nouveautés ?",
      a6: "Créez un compte gratuit et vous serez prévenu par e-mail de chaque nouveau parcours ou mise à jour de contenu.",
      q7: "Puis-je créer des profils pour ma famille ?",
      a7: "Oui ! Avec un compte Premium, vous pouvez créer jusqu'à 4 profils familiaux (1 principal + 3 membres). Chaque profil a sa propre progression, ses propres XP et sa propre série. Idéal pour apprendre en famille tout en gardant une petite compétition amicale !",
      q8: "Dans quelles langues puis-je étudier sur Spirify ?",
      a8: "Spirify est disponible en 6 langues : Français, English, Español, Italiano, Português et العربية. Tout le contenu (leçons, questions, explications) est entièrement traduit. Vous pouvez changer de langue à tout moment dans l'application."
    },

    // CTA
    cta: {
      title: "Prêt à commencer votre voyage de découverte ?",
      subtitle: "Rejoignez des milliers d'apprenants qui découvrent les religions de manière ludique, respectueuse et factuelle.",
      button: "Commencer maintenant",
      free: "Gratuit - Aucune carte de crédit requise"
    },

    // Testimonials
    testimonials: {
      badge: "Témoignages",
      title: "Ce qu'ils disent de Spirify",
      subtitle: "Des milliers d'utilisateurs découvrent les religions avec Spirify",
      testimonial1Text: "Une approche neutre et pédagogique qui m'a permis de mieux comprendre l'Islam. Les leçons sont courtes et très bien structurées.",
      testimonial1Name: "Marie L.",
      testimonial1Role: "Enseignante",
      testimonial2Text: "En tant que musulman, j'ai apprécié redécouvrir ma religion et explorer le christianisme et le judaïsme. Spirify favorise vraiment le dialogue.",
      testimonial2Name: "Ahmed K.",
      testimonial2Role: "Étudiant",
      testimonial3Text: "Je voulais comprendre les religions sans me sentir jugée. Spirify est parfait pour ça : neutre, respectueux et ludique !",
      testimonial3Name: "Sophie B.",
      testimonial3Role: "Designer",
      testimonial4Text: "Parfait pour mes cours d'histoire des religions. Mes élèves adorent l'approche ludique et objective de Spirify.",
      testimonial4Name: "Jean P.",
      testimonial4Role: "Professeur d'histoire",
      testimonial5Text: "En tant que juive, j'ai pu mieux comprendre l'histoire de ma religion et découvrir les autres traditions abrahamiques. Une vraie richesse !",
      testimonial5Name: "Rachel M.",
      testimonial5Role: "Médecin",
      testimonial6Text: "Application géniale pour comprendre le monde qui nous entoure. Les leçons sont courtes, parfaites pour mon emploi du temps chargé.",
      testimonial6Name: "Thomas D.",
      testimonial6Role: "Ingénieur",
      stat1: "Utilisateurs actifs",
      stat2: "Leçons disponibles",
      stat3: "Note moyenne",
      stat4: "Religions & Spiritualités"
    },

    // Footer
    footer: {
      copyright: "© 2024 Spirify – Plateforme d'apprentissage neutre des religions et de la spiritualité.",
      contact: "Contact",
      privacy: "Confidentialité",
      legal: "Mentions légales",
      rights: "Tous droits réservés."
    },


    // Animated Demo
    demo: {
      live: "EN DIRECT",
      play: "Lecture",
      pause: "Pause",
      step1: "Étape 1",
      step2: "Étape 2",
      step3: "Étape 3",
      step4: "Étape 4",
      step5: "Étape 5",
      welcome: "Bienvenue sur Spirify",
      choosePathSubtitle: "Choisissez votre parcours d'apprentissage",
      chooseYourPath: "Choisissez Votre Parcours",
      islamChristianismJudaism: "Islam, Christianisme ou Judaïsme",
      islamCourse: "Parcours Islam",
      fivePillars: "Les 5 Piliers de l'Islam",
      startFirstLesson: "Démarrez votre première leçon",
      shahada: "La Shahada - Profession de foi",
      discoverFirstPillar: "Découvrez le premier pilier de l'Islam",
      lessons: "leçons",
      exercises: "exercices",
      minutes: "min",
      start: "Commencer",
      interactiveQuestion: "Question Interactive",
      testKnowledge: "Testez vos connaissances",
      hearts: "Cœurs",
      whatMeansShahada: "Que signifie \"Shahada\" ?",
      ramadanFasting: "Le jeûne du Ramadan",
      professionOfFaith: "La profession de foi musulmane",
      dailyPrayer: "La prière quotidienne",
      pilgrimageToMecca: "Le pèlerinage à La Mecque",
      congrats: "Bravo ! Réponse Correcte",
      correctAnswer: "Excellente réponse ! La Shahada est effectivement la profession de foi, premier pilier de l'Islam.",
      gained: "Gagné",
      yourProgress: "Suivez Votre Progression",
      statsRealTime: "Statistiques et classement en temps réel",
      activeStreak: "Série active",
      days: "jours",
      totalEarned: "Total gagné",
      ranking: "Classement",
      continueLearning: "Continuer l'apprentissage",
      unlockMore: "Continuez pour débloquer plus de contenu"
    },

    // Dashboard
    dashboard: {
      welcome: "Bienvenue",
      chooseYourPath: "Choisissez votre parcours",
      yourProgress: "Votre progression",
      continueLesson: "Continuer",
      startLesson: "Commencer",
      locked: "Verrouillé",
      unlockAfter: "Débloquer après",
      completed: "Terminé",
      lessons: "leçons",
      xpEarned: "XP gagnés",
      streak: "jours d'affilée",
      hearts: "cœurs",
      level: "Niveau",
      todaysGoal: "Objectif du jour",
      heartsExplanation: "Vous perdez 1 cœur par erreur. Plus de cœurs = continuer à apprendre !",
      leaderboard: "Classement",
      gamification: "Progression",
      badges: "Badges",
      goals: "Objectifs",
      settings: "Paramètres",
      continue: "Continuer",
      start: "Commencer",
      resume: "Reprendre",
      questionPrompt: "Quelle religion souhaitez-vous découvrir aujourd'hui ?",
      clickToStart: "Cliquez pour commencer",
      questions: "Questions",
      // Course descriptions
      islamName: "Islam",
      islamDesc: "Découvrez les fondements de la foi musulmane, le Coran, les pratiques et l'histoire",
      christianismName: "Christianisme", 
      christianismDesc: "Explorez la Bible, les Évangiles, les sacrements et les traditions chrétiennes",
      judaismName: "Judaïsme",
      judaismDesc: "Comprenez la Torah, les fêtes juives, le Shabbat et l'histoire du peuple juif",
      spiritualityName: "Spiritualité & Dialogue",
      spiritualityDesc: "Découvrez les points communs, les valeurs universelles et l'approche comparative",
      bouddhismeName: "Bouddhisme",
      bouddhismeDesc: "Découvrez les enseignements du Bouddha, la méditation, les Quatre Nobles Vérités et le chemin vers l'éveil",
      hindouismeName: "Hindouisme",
      hindouismeDesc: "Explorez les Vedas, le karma, le dharma, les grandes divinités et les voies du yoga vers la libération",
      taoismeName: "Taoïsme",
      taoismeDesc: "Plongez dans la sagesse du Tao, le Wu Wei, l'harmonie Yin-Yang et la philosophie de Lao Tseu",
      transversalName: "Transversal",
      transversalDesc: "Parcours comparatifs et thématiques qui relient les traditions et approfondissent les grands sujets universels"
    },

    // Settings
    settings: {
      title: "Paramètres",
      profile: "Profil",
      username: "Nom d'utilisateur",
      email: "Email",
      status: "Statut",
      premium: "Premium",
      free: "Gratuit",
      language: "Langue",
      languageDescription: "Choisissez votre langue préférée pour l'interface",
      notifications: "Notifications",
      notificationsDescription: "Les paramètres de notifications seront bientôt disponibles.",
      privacy: "Confidentialité",
      privacyPolicy: "Politique de confidentialité",
      termsOfService: "Conditions d'utilisation",
      backToDashboard: "Retour au tableau de bord",
      loading: "Chargement..."
    },

    // Lesson
    lesson: {
      question: "Question",
      lessonProgress: "Leçon",
      loading: "Chargement de la leçon...",
      checkAnswer: "Vérifier",
      continue: "Continuer",
      continueLabel: "Continuer",
      nextQuestion: "Question suivante",
      finish: "Terminer la leçon",
      correct: "Excellent !",
      incorrect: "Pas tout à fait",
      explanation: "Explication",
      correctAnswer: "La bonne réponse était :",
      heartLost: "Vous avez perdu 1 cœur. Il vous reste",
      heart: "cœur",
      hearts: "cœurs",
      xpGained: "XP gagné",
      reviewMode: "Mode Révision",
      reviewIntro: "Vous avez fait quelques erreurs. Pas de panique ! Vous allez maintenant revoir seulement les questions où vous vous êtes trompé.",
      questionsToReview: "questions à revoir",
      reviewExplanation: "Répondez correctement pour valider la leçon !",
      heartsRemaining: "Cœurs restants :",
      startReview: "Commencer la révision",
      noHearts: "Plus de cœurs !",
      noHeartsMessage: "Vous avez perdu tous vos cœurs. Vous devez recommencer la leçon depuis le début.",
      tip: "Conseil",
      tipMessage: "Prenez le temps de bien lire les explications après chaque erreur!",
      restart: "Recommencer la leçon",
      backToDashboard: "Retour au tableau de bord",
      correctAnswers: "correctes",
      wellDone: "Bravo !",
      lessonCompleted: "Leçon terminée",
      heartsLeft: "Cœurs restants",
      learnMore: "Pour aller plus loin",
      nextLesson: "Leçon suivante",
      // Duolingo style micro-challenges
      yourTurn: "À toi de jouer",
      congrats: "Bravo !",
      oops: "Oups !",
      tryAgain: "Ce n'est pas ça, mais tu apprendras !",
      didYouKnow: "Le savais-tu ?",
      theCorrectAnswer: "La bonne réponse était :",
      perfectStreak: "Série parfaite",
      // Mascot & completion screen messages
      mascotCelebration: "INCROYABLE !",
      mascotCelebrationSub: "Tu es un champion !",
      mascotHappy: "BRAVO !",
      mascotHappySub: "Excellent travail !",
      mascotEncouraging: "BIEN JOUÉ !",
      mascotEncouragingSub: "Continue comme ça !",
      mascotSupportive: "BON EFFORT !",
      mascotSupportiveSub: "Tu vas y arriver !",
      mascotContinue: "Continuer",
      perfect: "Parfait !",
      perfectSub: "Tu as tout réussi sans erreur !",
      excellent: "Excellent !",
      excellentSub: "Tu maîtrises vraiment ce sujet !",
      goodJob: "Bien joué !",
      goodJobSub: "Continue comme ça !",
      notBad: "Pas mal !",
      notBadSub: "Tu progresses, continue !",
      keepGoing: "Continue !",
      keepGoingSub: "La pratique fait le maître !",
      accuracy: "Précision",
      // Combo messages
      combo3: "En feu !",
      combo5: "Incroyable !",
      combo7: "Superstar !",
      combo10: "Légendaire !",
      comboPerfect: "Parfait !",
      comboStreak: "{streak} bonnes réponses d'affilée !",
      comboPerfectSub: "100% de réussite !",
      skipReview: "Passer et voir le résumé",
      finishReview: "Terminer la révision"
    },

    // Family
    family: {
      title: "Famille",
      subtitle: "Apprenez ensemble, à votre rythme.",
      members: "Membres",
      rankings: "Classement",
      badges: "Badges",
      groupTitle: "Groupe famille",
      groupSubtitle: "Jusqu'à 4 profils sur le même compte.",
      addProfile: "Ajouter un profil",
      createProfile: "Créer un profil",
      editProfile: "Modifier le profil",
      inviteFamily: "Invite ta famille",
      inviteMessage: "Crée une petite compétition amicale pour apprendre plus régulièrement.",
      familyRankings: "Classement famille",
      rankingsObjective: "Objectif : progresser ensemble, sans pression.",
      familyBadges: "Badges famille",
      badgesMessage: "Des récompenses pour la constance et l'engagement.",
      familyStats: "Statistiques famille",
      weeklyXP: "XP cette semaine",
      monthlyXP: "XP ce mois",
      streak: "Série",
      week: "Semaine",
      month: "Mois",
      pseudo: "Pseudo",
      pseudoPlaceholder: "Ex: Maman, Papa, Lucas...",
      avatar: "Avatar",
      main: "Principal",
      earned: "Obtenu",
      toUnlock: "À débloquer",
      totalMembers: "Membres",
      totalXP: "XP cumulés",
      newProfile: "Nouveau profil",
      cancel: "Annuler",
      create: "Créer",
      save: "Enregistrer",
      edit: "Modifier",
      delete: "Supprimer",
      deleteConfirm: "Supprimer ce profil ? Cette action est irréversible.",
      profileCreated: "Profil créé !",
      profileUpdated: "Profil mis à jour !",
      profileDeleted: "Profil supprimé",
      profilesLabel: "profils",
      days: "jours",
      daysShort: "j",
      you: "toi",
      pseudoRequired: "Le pseudo est requis",
      active: "Actif",
      switchedTo: "Profil actif",
      whoIsLearning: "Qui apprend aujourd'hui ?",
      clickToSwitch: "Cliquez pour activer ce profil",
      activeProfile: "Profil actif",
      switchProfile: "Changer"
    },

    // Leaderboard
    leaderboard: {
      title: "Classement",
      subtitle: "Progresse chaque jour et grimpe dans le classement.",
      yourRank: "Ta position",
      topPlayers: "Top 50 apprenants",
      weekly: "Semaine",
      monthly: "Mois",
      allTime: "Général",
      weeklyTitle: "Classement de la semaine",
      monthlyTitle: "Classement du mois",
      allTimeTitle: "Classement général",
      annualChallenge: "Challenge annuel",
      annualDescription: "Chaque année, les 3 premiers du classement global sont mis à l'honneur.",
      currentPodium: "Podium actuel",
      rewards: "Récompenses",
      specialBadge: "Badge spécial",
      crownOnAvatar: "Couronne sur l'avatar pendant l'année suivante",
      youAreTop: "Tu es dans le Top 3 ! Continue comme ça !",
      keepLearning: "Continue à apprendre pour rejoindre le podium !",
      close: "Fermer",
      you: "toi",
      xpTotal: "XP total",
      xpWeek: "XP semaine",
      xpMonth: "XP mois",
      noData: "Aucun classement disponible pour cette période.",
      annual: "Annuel"
    },

    // Auth
    auth: {
      login: "Se connecter",
      signup: "S'inscrire",
      email: "Email",
      password: "Mot de passe",
      username: "Nom d'utilisateur",
      forgotPassword: "Mot de passe oublié ?",
      noAccount: "Pas encore de compte ?",
      haveAccount: "Déjà un compte ?",
      loginButton: "Connexion",
      signupButton: "Inscription",
      or: "ou",
      welcomeBack: "Bon retour !",
      loginSubtitle: "Connectez-vous pour continuer votre apprentissage",
      loggingIn: "Connexion...",
      createAccount: "Créer un compte gratuitement",
      emailPlaceholder: "vous@exemple.com",
      errorInvalid: "Email ou mot de passe incorrect",
      signupTitle: "Commencez votre aventure",
      signupSubtitle: "Créez votre compte gratuit et découvrez les religions",
      confirmPassword: "Confirmer le mot de passe",
      creating: "Création...",
      errorPasswordLength: "Le mot de passe doit contenir au moins 6 caractères",
      errorPasswordMatch: "Les mots de passe ne correspondent pas",
      errorUsernameLength: "Le nom d'utilisateur doit contenir au moins 3 caractères",
      errorSignup: "Erreur lors de la création du compte",
      passwordStrength: "Force du mot de passe",
      weak: "Faible",
      medium: "Moyen",
      strong: "Fort",
      termsPrefix: "En créant un compte, vous acceptez nos",
      termsLink: "conditions d'utilisation",
      alreadyHaveAccount: "Vous avez déjà un compte ?",
      backToHome: "← Retour à l'accueil",
      continueWithGoogle: "Continuer avec Google",
      or: "ou"
    },

    // Pricing
    pricing: {
      title: "Apprenez les religions du monde, jouez en famille et pratiquez des langues.",
      subtitle: "Une expérience ludique, culturelle et multilingue, pour tous les âges.",
      badge: "Tarifs simples et transparents",
      free: "Gratuit",
      premium: "Premium",
      monthly: "Mensuel",
      annual: "Annuel",
      perMonth: "par mois",
      perYear: "par an",
      forever: "pour toujours",
      subscribe: "S'abonner",
      features: "Fonctionnalités",
      mostPopular: "Le plus populaire",
      startFree: "Commencer gratuitement",
      goPremium: "Débloquer l'expérience complète",
      savePercent: "Économisez 33%",
      annualPrice: "39,99€/an",
      // Free plan
      freeDescription: "Découverte complète pour commencer.",
      lessonsCount: "24 leçons (6 par cours)",
      interactiveQuestions: "Questions interactives",
      basicGamification: "Gamification de base",
      heartsPerDay: "5 cœurs par jour",
      publicLeaderboard: "Classement public",
      // Premium plan
      premiumDescription: "Débloquez l'expérience complète : apprentissage + langues + compétition.",
      premiumSubtitle: "Jusqu'à 4 profils inclus — idéal pour les familles.",
      everythingFree: "Tout du plan Gratuit",
      familyMode: "Mode Famille (jusqu'à 4 profils)",
      allLanguages: "Accès à toutes les langues",
      unlimitedAccess: "Accès illimité à tous les parcours",
      annualChallenges: "Classements & challenges annuels",
      unlimitedHearts: "Cœurs illimités",
      downloadCertificates: "Certificats téléchargeables",
      offlineMode: "Mode hors-ligne",
      prioritySupport: "Support prioritaire",
      familyTip: "Astuce : lancez une compétition amicale en famille avec le classement interne (semaine / mois / streak).",
      // Locked features (free plan)
      lockedFamilyMode: "Mode Famille (jusqu'à 4 profils)",
      lockedAllLanguages: "Accès à toutes les langues",
      lockedChallenges: "Challenges annuels & exclusifs",
      lockedAdvancedLevels: "Niveaux avancés",
      lockedOfflineMode: "Mode hors-ligne",
      // Footer
      footerNote: "Spirify est une expérience ludique et respectueuse. Aucun prosélytisme.",
      // CTA
      ctaTitle: "Prêt à commencer votre voyage spirituel ?",
      ctaSubtitle: "Rejoignez des milliers d'apprenants qui découvrent les religions de manière ludique et respectueuse.",
      noCreditCard: "Aucune carte de crédit requise",
      redirecting: "Redirection vers le paiement...",
      login: "Se connecter"
    },

    leaderboardPage: {
      loading: "Chargement du classement...",
      yourRank: "Votre classement",
      outOf: "sur",
      learners: "apprenants",
      days: "jours",
      top3: "Top 3",
      ranking: "Classement général",
      otherRanked: "Autres classés",
      level: "Niveau"
    },

    contact: {
      title: "Contactez-nous",
      subtitle: "Une question ? Un problème ? N'hésitez pas à nous écrire.",
      name: "Nom",
      namePlaceholder: "Votre nom",
      email: "Email",
      emailPlaceholder: "votre@email.com",
      subject: "Sujet",
      subjectPlaceholder: "Sujet de votre message",
      message: "Message",
      messagePlaceholder: "Votre message...",
      send: "Envoyer",
      sending: "Envoi en cours...",
      thankYou: "Merci !",
      messageSent: "Votre message a bien été envoyé. Nous vous répondrons dans les plus brefs délais.",
      backToDashboard: "Retour au tableau de bord"
    },

    privacy: {
      title: "Politique de confidentialité",
      lastUpdate: "Dernière mise à jour",
      intro: "Chez Spirify, nous accordons une grande importance à la protection de vos données personnelles. Cette politique de confidentialité explique quelles informations nous collectons, comment nous les utilisons et quels sont vos droits.",
      dataCollected: "Données collectées",
      dataCollectedText: "Nous collectons les données suivantes :",
      data1: "Informations de compte : nom d'utilisateur, adresse email",
      data2: "Données de progression : leçons complétées, scores, XP",
      data3: "Données techniques : type de navigateur, adresse IP (anonymisée)",
      dataUsage: "Utilisation des données",
      dataUsageText: "Vos données sont utilisées pour :",
      usage1: "Fournir et améliorer nos services",
      usage2: "Personnaliser votre expérience d'apprentissage",
      usage3: "Vous envoyer des notifications importantes",
      usage4: "Établir des statistiques anonymisées",
      dataProtection: "Protection des données",
      dataProtectionText: "Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles appropriées pour protéger vos données contre tout accès non autorisé, modification, divulgation ou destruction. Vos données sont stockées sur des serveurs sécurisés et chiffrées.",
      yourRights: "Vos droits",
      yourRightsText: "Conformément au RGPD, vous disposez des droits suivants :",
      right1: "Droit d'accès à vos données",
      right2: "Droit de rectification",
      right3: "Droit à l'effacement (droit à l'oubli)",
      right4: "Droit à la portabilité des données",
      right5: "Droit d'opposition au traitement",
      cookies: "Cookies",
      cookiesText: "Nous utilisons des cookies essentiels pour le fonctionnement du site (authentification, préférences de langue). Aucun cookie publicitaire ou de tracking n'est utilisé.",
      contact: "Contact",
      contactText: "Pour exercer vos droits ou pour toute question concernant vos données personnelles, contactez-nous :",
      back: "Retour"
    },

    legal: {
      title: "Mentions légales",
      editor: "Éditeur du site",
      hosting: "Hébergement",
      intellectualProperty: "Propriété intellectuelle",
      intellectualPropertyText: "L'ensemble de ce site relève de la législation française et internationale sur le droit d'auteur et la propriété intellectuelle. Tous les droits de reproduction sont réservés, y compris pour les documents téléchargeables et les représentations iconographiques et photographiques.",
      personalData: "Données personnelles",
      personalDataText: "Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez d'un droit d'accès, de rectification et de suppression des données vous concernant. Pour exercer ce droit, contactez-nous à l'adresse : contact@spirify.ai",
      cookies: "Cookies",
      cookiesText: "Ce site utilise des cookies pour améliorer l'expérience utilisateur et réaliser des statistiques de visites. En continuant à naviguer sur ce site, vous acceptez l'utilisation de cookies.",
      liability: "Responsabilité",
      liabilityText: "Les informations fournies sur ce site le sont à titre informatif. MFP Services France ne saurait garantir l'exactitude, la complétude ou l'actualité des informations diffusées sur le site.",
      lastUpdate: "Dernière mise à jour"
    }
  },

  en: {
    // Navigation
    nav: {
      audience: "Who is it for",
      howItWorks: "How it works",
      paths: "Paths",
      faq: "FAQ",
      pricing: "Pricing",
      startFree: "Start for free",
      myDashboard: "My Dashboard",
      back: "Back"
    },
    
    // Hero Section
    hero: {
      badge: "A new way to learn about religions",
      title: "Discover religions in",
      titleHighlight: "5 minutes a day",
      description: "Spirify is a fun and respectful platform to discover religions, share learning with family and practice languages through cultural content, without proselytism.",
      subtitle: "Spirify is a fun and respectful platform to discover religions, share learning with family and practice languages through cultural content, without proselytism.",
      feature1: "<strong class='text-purple-700'>5-10 minute</strong> micro-lessons",
      feature2: "<strong class='text-purple-700'>5+ religions</strong> and spiritualities",
      feature3: "<strong class='text-purple-700'>Comparative</strong> and respectful approach",
      feature4: "<strong class='text-purple-700'>Gamification</strong> (XP, levels, streaks)",
      cta: "Start for free",
      ctaSecondary: "See how it works",
      noCommitment: "No commitment, immediate access to the discovery version."
    },

    // Audience Section
    audience: {
      title: "Who is Spirify for?",
      subtitle: "Spirify is for anyone who wants to understand religions without getting into complex theological debates. You don't need to be a believer or a specialist: you just need to be curious.",
      card1Title: "Curious & seekers",
      card1Text: "You want to better understand the religions we hear about in the news, in your family or in society, without having time to read hundreds of pages.",
      card2Title: "Believers who want to deepen",
      card2Text: "You already practice a religion but want to better understand its history, texts, practices – and discover how other traditions work.",
      card3Title: "Students, educators & mediators",
      card3Text: "You work with young people, diverse audiences or in intercultural contexts, and need a simple, neutral and educational tool to talk about religion."
    },

    // How It Works
    howItWorks: {
      title: "How does it work?",
      subtitle: "The experience is designed as an educational game: you choose a religion or a cross-cutting path, then progress by unlocking levels, lessons and badges.",
      step1Title: "Choose your path",
      step1Text: "Islam, Christianity, Judaism or \"Spirituality & Dialogue\": select the path that interests you most, or combine them.",
      step2Title: "Follow daily micro-lessons",
      step2Text: "Each lesson lasts between 5 and 10 minutes, with a mix of simple texts, quizzes, MCQs and concrete examples.",
      step3Title: "Progress and unlock badges",
      step3Text: "You earn experience points (XP), unlock new chapters and track your progress day by day.",
      videoBadge: "See in action",
      videoTitle: "Discover Spirify in 2 minutes",
      videoSubtitle: "Browse a complete lesson and see how learning becomes a captivating game",
      benefit1: "5 min per day",
      benefit1Sub: "Learn at your own pace, no pressure",
      benefit2: "Gamification",
      benefit2Sub: "XP, badges and motivating rankings",
      benefit3: "Clear progress",
      benefit3Sub: "Track your progress in real time",
      demoTitle: "Discover our interactive platform",
      demoSubtitle: "Learn at your own pace with gamified lessons, interactive quizzes and track your progress in real time.",
      startFree: "Start for free"
    },

    // Paths
    paths: {
      title: "Structured paths for each religion",
      subtitle: "Each tradition is presented through clear modules: basics, texts, rites, celebrations, history and contemporary issues.",
      badge: "Path",
      islam: "Islam",
      islamDesc: "Basics of faith, Quran and surahs, prayer (salat), fasting, celebrations, history and diversity of Muslim practices.",
      islamItem1: "Basics of faith and pillars",
      islamItem2: "Quran structure and surahs",
      islamItem3: "Prayer, fasting, celebrations",
      islamItem4: "History and contemporary diversity",
      christianity: "Christianity",
      christianityDesc: "Bible and Gospels, main branches, prayer, sacraments, Christian celebrations, history and current issues.",
      christianityItem1: "Bible, New Testament, Gospels",
      christianityItem2: "Branches and traditions",
      christianityItem3: "Prayer, sacraments, celebrations",
      christianityItem4: "History and contemporary world",
      judaism: "Judaism",
      judaismDesc: "Torah, Tanakh, Jewish celebrations, Shabbat, community practices, history of the Jewish people.",
      judaismItem1: "Texts: Torah and Tanakh",
      judaismItem2: "Shabbat and community life",
      judaismItem3: "Celebrations and calendar",
      judaismItem4: "Major historical stages",
      spirituality: "Spirituality & dialogue",
      spiritualityDesc: "Common values, ethics, compassion, justice, interiority, and comparative modules.",
      spiritualityItem1: "Values and ethics",
      spiritualityItem2: "Prayer and interiority",
      spiritualityItem3: "Comparisons of practices",
      spiritualityItem4: "Dialogue and mutual understanding",
      buddhism: "Buddhism",
      buddhismDesc: "The 4 Noble Truths, the Eightfold Path, meditation, karma, Nirvana and Buddhist traditions.",
      buddhismItem1: "The 4 Noble Truths",
      buddhismItem2: "The Eightfold Path",
      buddhismItem3: "Meditation and practice",
      buddhismItem4: "Schools and traditions",
      hinduism: "Hinduism",
      hinduismDesc: "Vedas, Upanishads, karma, dharma, yoga, deities and Hindu spiritual practices.",
      hinduismItem1: "Sacred texts and Vedas",
      hinduismItem2: "Karma, dharma and moksha",
      hinduismItem3: "Yoga and meditation",
      hinduismItem4: "Deities and rituals",
      taoism: "Taoism",
      taoismDesc: "The Tao, Wu Wei, Yin-Yang, Zhuangzi, harmony with nature and Eastern wisdom.",
      taoismItem1: "The Tao Te Ching",
      taoismItem2: "Wu Wei and non-action",
      taoismItem3: "Yin and Yang",
      taoismItem4: "Wisdom and harmony",
      transversal: "Transversal",
      transversalDesc: "Comparative paths connecting traditions: non-duality, suffering, right action.",
      transversalItem1: "Non-duality & illusion",
      transversalItem2: "Suffering & liberation",
      transversalItem3: "Right action",
      transversalItem4: "Bridges between traditions",
      newBadge: "New",
      approach: "Each path is designed with a factual and respectful approach, without judgment or doctrinal position."
    },

    // Features
    features: {
      title: "Simple and gamified pedagogy",
      subtitle: "The goal is to make learning about religions as smooth and motivating as learning a language.",
      microLessons: "Micro-lessons",
      microLessonsText: "Very short lessons to progress even with a busy schedule.",
      quizzes: "Interactive quizzes",
      quizzesText: "MCQs, true/false, concept associations to check understanding.",
      comparative: "Comparative approach",
      comparativeText: "Modules that show the commonalities and differences between Islam, Christianity and Judaism.",
      gamification: "Gamification",
      gamificationText: "Experience points, levels, consecutive day streaks, thematic badges.",
      progress: "Progress tracking",
      progressText: "Simple dashboard with progress by path, number of lessons completed.",
      revision: "\"Revision\" mode",
      revisionText: "Ability to quickly replay quizzes to review key concepts."
    },

    // FAQ
    faq: {
      title: "Frequently asked questions",
      subtitle: "Some answers to the most common questions about Spirify.",
      q1: "Does Spirify promote a religion?",
      a1: "No. Spirify is an educational platform. It presents the religions studied in a factual and respectful manner, without proselytism or judgment.",
      q2: "Do I need to be a believer to use Spirify?",
      a2: "No. The platform is for both believers and non-believers, curious or seeking understanding.",
      q3: "Who creates the content?",
      a3: "The content is developed from recognized sources and can be reviewed by people trained in religious studies to ensure a balanced presentation.",
      q4: "Are the seven religions treated equally?",
      a4: "Yes. Each religion (Islam, Christianity, Judaism, Buddhism, Hinduism, Taoism, Spirituality) has its own structured path in the same way, with as many lessons and exercises.",
      q5: "Will there be other religions in the future?",
      a5: "Yes! We continuously enrich our platform. Recently, we added Buddhism, Hinduism and Taoism with 20 lessons each, including Expert levels. Other religions and spiritual traditions may be added based on demand.",
      q6: "How can I be informed of the launch?",
      a6: "You can create a free account right now. You will be notified of new features by email.",
      q7: "Can I create profiles for my family?",
      a7: "Yes! With a Premium account, you can create up to 4 family profiles (1 main + 3 members). Each profile has its own progress, XP and streak. Perfect for learning together while keeping a friendly competition!",
      q8: "What languages can I study in on Spirify?",
      a8: "Spirify is available in 6 languages: Français, English, Español, Italiano, Português and العربية. All content (lessons, questions, explanations) is fully translated. You can switch languages at any time in the app."
    },

    // CTA
    cta: {
      title: "Ready to start your discovery journey?",
      subtitle: "Join thousands of learners discovering religions in a fun, respectful and factual way.",
      button: "Start now",
      free: "Free - No credit card required"
    },

    // Testimonials
    testimonials: {
      badge: "Testimonials",
      title: "What they say about Spirify",
      subtitle: "Thousands of users are discovering religions with Spirify",
      testimonial1Text: "A neutral and educational approach that helped me better understand Islam. The lessons are short and very well structured.",
      testimonial1Name: "Marie L.",
      testimonial1Role: "Teacher",
      testimonial2Text: "As a Muslim, I enjoyed rediscovering my religion and exploring Christianity and Judaism. Spirify really promotes dialogue.",
      testimonial2Name: "Ahmed K.",
      testimonial2Role: "Student",
      testimonial3Text: "I wanted to understand religions without feeling judged. Spirify is perfect for that: neutral, respectful and fun!",
      testimonial3Name: "Sophie B.",
      testimonial3Role: "Designer",
      testimonial4Text: "Perfect for my religious history classes. My students love Spirify's fun and objective approach.",
      testimonial4Name: "Jean P.",
      testimonial4Role: "History teacher",
      testimonial5Text: "As a Jew, I was able to better understand the history of my religion and discover other Abrahamic traditions. A real treasure!",
      testimonial5Name: "Rachel M.",
      testimonial5Role: "Doctor",
      testimonial6Text: "Amazing app to understand the world around us. The lessons are short, perfect for my busy schedule.",
      testimonial6Name: "Thomas D.",
      testimonial6Role: "Engineer",
      stat1: "Active users",
      stat2: "Available lessons",
      stat3: "Average rating",
      stat4: "Religions & Spiritualities"
    },

    // Demo
    demo: {
      live: "LIVE",
      play: "Play",
      pause: "Pause",
      step1: "Step 1",
      step2: "Step 2",
      step3: "Step 3",
      step4: "Step 4",
      step5: "Step 5",
      welcome: "Welcome to Spirify",
      choosePathSubtitle: "Choose your learning path",
      chooseYourPath: "Choose your path",
      islamChristianismJudaism: "Islam, Christianity or Judaism",
      islamCourse: "Islam Course",
      fivePillars: "The Five Pillars of Islam",
      startFirstLesson: "Start your first lesson",
      shahada: "Shahada - Profession of Faith",
      discoverFirstPillar: "Discover the first pillar of Islam",
      lessons: "lessons",
      exercises: "exercises",
      minutes: "min",
      start: "Start",
      interactiveQuestion: "Interactive Question",
      testKnowledge: "Test your knowledge",
      hearts: "hearts",
      whatMeansShahada: "What does \"Shahada\" mean?",
      ramadanFasting: "Ramadan fasting",
      professionOfFaith: "Islamic profession of faith",
      dailyPrayer: "Daily prayer",
      pilgrimageToMecca: "Pilgrimage to Mecca",
      congrats: "Well done! Correct answer",
      correctAnswer: "Excellent answer! The Shahada is indeed the profession of faith, the first pillar of Islam.",
      gained: "gained",
      yourProgress: "Track your progress",
      statsRealTime: "Statistics and ranking in real time",
      activeStreak: "Active streak",
      days: "days",
      totalEarned: "Total earned",
      ranking: "Ranking",
      continueLearning: "Continue learning",
      unlockMore: "Continue to unlock more content"
    },

    // Footer
    footer: {
      copyright: "© 2024 Spirify – Neutral learning platform for religions and spirituality.",
      contact: "Contact",
      privacy: "Privacy",
      legal: "Legal notice",
      rights: "All rights reserved."
    },

    // Dashboard
    dashboard: {
      welcome: "Welcome",
      chooseYourPath: "Choose your path",
      yourProgress: "Your progress",
      continueLesson: "Continue",
      startLesson: "Start",
      locked: "Locked",
      unlockAfter: "Unlock after",
      completed: "Completed",
      lessons: "lessons",
      xpEarned: "XP earned",
      streak: "day streak",
      hearts: "hearts",
      level: "Level",
      todaysGoal: "Today's goal",
      heartsExplanation: "You lose 1 heart per mistake. More hearts = keep learning!",
      leaderboard: "Leaderboard",
      gamification: "Progress",
      badges: "Badges",
      goals: "Goals",
      settings: "Settings",
      continue: "Continue",
      start: "Start",
      resume: "Resume",
      questionPrompt: "Which religion would you like to discover today?",
      clickToStart: "Click to start",
      questions: "Questions",
      // Course descriptions
      islamName: "Islam",
      islamDesc: "Discover the foundations of Muslim faith, the Quran, practices and history",
      christianismName: "Christianity",
      christianismDesc: "Explore the Bible, Gospels, sacraments and Christian traditions",
      judaismName: "Judaism",
      judaismDesc: "Understand the Torah, Jewish festivals, Shabbat and the history of the Jewish people",
      spiritualityName: "Spirituality & Dialogue",
      spiritualityDesc: "Discover common ground, universal values and comparative approaches",
      bouddhismeName: "Buddhism",
      bouddhismeDesc: "Discover the Buddha's teachings, meditation, the Four Noble Truths and the path to enlightenment",
      hindouismeName: "Hinduism",
      hindouismeDesc: "Explore the Vedas, karma, dharma, major deities and the paths of yoga towards liberation",
      taoismeName: "Taoism",
      taoismeDesc: "Dive into the wisdom of the Tao, Wu Wei, Yin-Yang harmony and Lao Tzu's philosophy",
      transversalName: "Transversal",
      transversalDesc: "Comparative and thematic paths that connect traditions and explore universal topics"
    },

    // Settings
    settings: {
      title: "Settings",
      profile: "Profile",
      username: "Username",
      email: "Email",
      status: "Status",
      premium: "Premium",
      free: "Free",
      language: "Language",
      languageDescription: "Choose your preferred language for the interface",
      notifications: "Notifications",
      notificationsDescription: "Notification settings will be available soon.",
      privacy: "Privacy",
      privacyPolicy: "Privacy Policy",
      termsOfService: "Terms of Service",
      backToDashboard: "Back to Dashboard",
      loading: "Loading..."
    },

    // Lesson
    lesson: {
      question: "Question",
      lessonProgress: "Lesson",
      loading: "Loading lesson...",
      checkAnswer: "Check",
      continue: "Continue",
      nextQuestion: "Next question",
      finish: "Finish lesson",
      correct: "Excellent!",
      incorrect: "Not quite",
      explanation: "Explanation",
      correctAnswer: "The correct answer was:",
      heartLost: "You lost 1 heart. You have",
      heart: "heart",
      hearts: "hearts",
      xpGained: "XP gained",
      reviewMode: "Review Mode",
      reviewIntro: "You made a few mistakes. Don't panic! You will now review only the questions you got wrong.",
      questionsToReview: "questions to review",
      reviewExplanation: "Answer correctly to complete the lesson!",
      heartsRemaining: "Hearts remaining:",
      startReview: "Start review",
      noHearts: "No hearts left!",
      noHeartsMessage: "You lost all your hearts. You must restart the lesson from the beginning.",
      tip: "Tip",
      tipMessage: "Take time to read the explanations after each mistake!",
      restart: "Restart lesson",
      backToDashboard: "Back to dashboard",
      correctAnswers: "correct",
      wellDone: "Well done!",
      lessonCompleted: "Lesson completed",
      heartsLeft: "Hearts left",
      learnMore: "Learn more",
      nextLesson: "Next lesson",
      backToDashboard: "Back to dashboard",
      // Duolingo style micro-challenges
      yourTurn: "Your turn",
      congrats: "Well done!",
      oops: "Oops!",
      tryAgain: "That's not it, but you'll learn!",
      didYouKnow: "Did you know?",
      theCorrectAnswer: "The correct answer was:",
      perfectStreak: "Perfect streak",
      continueLabel: "Continue",
      // Mascot & completion screen messages
      mascotCelebration: "INCREDIBLE!",
      mascotCelebrationSub: "You are a champion!",
      mascotHappy: "WELL DONE!",
      mascotHappySub: "Excellent work!",
      mascotEncouraging: "GOOD JOB!",
      mascotEncouragingSub: "Keep it up!",
      mascotSupportive: "GOOD EFFORT!",
      mascotSupportiveSub: "You will get there!",
      mascotContinue: "Continue",
      perfect: "Perfect!",
      perfectSub: "You got everything right!",
      excellent: "Excellent!",
      excellentSub: "You really mastered this topic!",
      goodJob: "Good job!",
      goodJobSub: "Keep it up!",
      notBad: "Not bad!",
      notBadSub: "You're making progress, keep going!",
      keepGoing: "Keep going!",
      keepGoingSub: "Practice makes perfect!",
      accuracy: "Accuracy",
      // Combo messages
      combo3: "On fire!",
      combo5: "Incredible!",
      combo7: "Superstar!",
      combo10: "Legendary!",
      comboPerfect: "Perfect!",
      comboStreak: "{streak} correct answers in a row!",
      comboPerfectSub: "100% success!",
      skipReview: "Skip and see summary",
      finishReview: "Finish review"
    },

    // Family
    family: {
      title: "Family",
      subtitle: "Learn together, at your own pace.",
      members: "Members",
      rankings: "Rankings",
      badges: "Badges",
      groupTitle: "Family group",
      groupSubtitle: "Up to 4 profiles on the same account.",
      addProfile: "Add profile",
      createProfile: "Create profile",
      editProfile: "Edit profile",
      inviteFamily: "Invite your family",
      inviteMessage: "Create a friendly competition to learn more regularly.",
      familyRankings: "Family rankings",
      rankingsObjective: "Goal: progress together, no pressure.",
      familyBadges: "Family badges",
      badgesMessage: "Rewards for consistency and engagement.",
      familyStats: "Family statistics",
      weeklyXP: "XP this week",
      monthlyXP: "XP this month",
      streak: "Streak",
      week: "Week",
      month: "Month",
      pseudo: "Nickname",
      pseudoPlaceholder: "Ex: Mom, Dad, Lucas...",
      avatar: "Avatar",
      main: "Main",
      earned: "Earned",
      toUnlock: "To unlock",
      totalMembers: "Members",
      totalXP: "Total XP",
      newProfile: "New profile",
      cancel: "Cancel",
      create: "Create",
      save: "Save",
      edit: "Edit",
      delete: "Delete",
      deleteConfirm: "Delete this profile? This action is irreversible.",
      profileCreated: "Profile created!",
      profileUpdated: "Profile updated!",
      profileDeleted: "Profile deleted",
      profilesLabel: "profiles",
      days: "days",
      daysShort: "d",
      you: "you",
      pseudoRequired: "Nickname is required",
      active: "Active",
      switchedTo: "Active profile",
      whoIsLearning: "Who's learning today?",
      clickToSwitch: "Click to switch to this profile",
      activeProfile: "Active profile",
      switchProfile: "Switch"
    },

    // Leaderboard
    leaderboard: {
      title: "Leaderboard",
      subtitle: "Progress every day and climb the rankings.",
      yourRank: "Your rank",
      topPlayers: "Top 50 learners",
      weekly: "Week",
      monthly: "Month",
      allTime: "All time",
      weeklyTitle: "Weekly rankings",
      monthlyTitle: "Monthly rankings",
      allTimeTitle: "All time rankings",
      annualChallenge: "Annual challenge",
      annualDescription: "Each year, the top 3 in the global ranking are honored.",
      currentPodium: "Current podium",
      rewards: "Rewards",
      specialBadge: "Special badge",
      crownOnAvatar: "Crown on avatar for the following year",
      youAreTop: "You're in the Top 3! Keep it up!",
      keepLearning: "Keep learning to reach the podium!",
      close: "Close",
      you: "you",
      xpTotal: "Total XP",
      xpWeek: "Week XP",
      xpMonth: "Month XP",
      noData: "No leaderboard data available for this period.",
      annual: "Annual"
    },

    // Auth
    auth: {
      login: "Log in",
      signup: "Sign up",
      email: "Email",
      password: "Password",
      username: "Username",
      forgotPassword: "Forgot password?",
      noAccount: "Don't have an account?",
      haveAccount: "Already have an account?",
      loginButton: "Log in",
      signupButton: "Sign up",
      or: "or",
      welcomeBack: "Welcome back!",
      loginSubtitle: "Log in to continue your learning",
      loggingIn: "Logging in...",
      createAccount: "Create a free account",
      emailPlaceholder: "you@example.com",
      errorInvalid: "Invalid email or password",
      signupTitle: "Start your adventure",
      signupSubtitle: "Create your free account and discover religions",
      confirmPassword: "Confirm password",
      creating: "Creating...",
      errorPasswordLength: "Password must be at least 6 characters",
      errorPasswordMatch: "Passwords do not match",
      errorUsernameLength: "Username must be at least 3 characters",
      errorSignup: "Error creating account",
      passwordStrength: "Password strength",
      weak: "Weak",
      medium: "Medium",
      strong: "Strong",
      termsPrefix: "By creating an account, you accept our",
      termsLink: "terms of use",
      alreadyHaveAccount: "Already have an account?",
      backToHome: "← Back to home",
      continueWithGoogle: "Continue with Google",
      or: "or"
    },

    // Pricing
    pricing: {
      title: "Learn world religions, play with family, and practice languages.",
      subtitle: "A fun, cultural, and multilingual experience for all ages.",
      badge: "Simple and transparent pricing",
      free: "Free",
      premium: "Premium",
      monthly: "Monthly",
      annual: "Annual",
      perMonth: "per month",
      perYear: "per year",
      forever: "forever",
      subscribe: "Subscribe",
      features: "Features",
      mostPopular: "Most popular",
      startFree: "Start for free",
      goPremium: "Unlock the full experience",
      savePercent: "Save 33%",
      annualPrice: "€39.99/year",
      // Free plan
      freeDescription: "Complete discovery to get started.",
      lessonsCount: "24 lessons (6 per course)",
      interactiveQuestions: "Interactive questions",
      basicGamification: "Basic gamification",
      heartsPerDay: "5 hearts per day",
      publicLeaderboard: "Public leaderboard",
      // Premium plan
      premiumDescription: "Unlock the full experience: learning + languages + competition.",
      premiumSubtitle: "Up to 4 profiles included — ideal for families.",
      everythingFree: "Everything in Free plan",
      familyMode: "Family Mode (up to 4 profiles)",
      allLanguages: "Access to all languages",
      unlimitedAccess: "Unlimited access to all courses",
      annualChallenges: "Rankings & annual challenges",
      unlimitedHearts: "Unlimited hearts",
      downloadCertificates: "Downloadable certificates",
      offlineMode: "Offline mode",
      prioritySupport: "Priority support",
      familyTip: "Tip: Start a friendly family competition with the internal ranking (week / month / streak).",
      // Locked features (free plan)
      lockedFamilyMode: "Family Mode (up to 4 profiles)",
      lockedAllLanguages: "Access to all languages",
      lockedChallenges: "Annual & exclusive challenges",
      lockedAdvancedLevels: "Advanced levels",
      lockedOfflineMode: "Offline mode",
      // Footer
      footerNote: "Spirify is a fun and respectful experience. No proselytizing.",
      // CTA
      ctaTitle: "Ready to start your spiritual journey?",
      ctaSubtitle: "Join thousands of learners discovering religions in a fun and respectful way.",
      noCreditCard: "No credit card required",
      redirecting: "Redirecting to payment...",
      login: "Log in"
    },

    leaderboardPage: {
      loading: "Loading leaderboard...",
      yourRank: "Your rank",
      outOf: "out of",
      learners: "learners",
      days: "days",
      top3: "Top 3",
      ranking: "General ranking",
      otherRanked: "Other ranked",
      level: "Level"
    },

    contact: {
      title: "Contact us",
      subtitle: "Have a question? A problem? Don't hesitate to write to us.",
      name: "Name",
      namePlaceholder: "Your name",
      email: "Email",
      emailPlaceholder: "your@email.com",
      subject: "Subject",
      subjectPlaceholder: "Subject of your message",
      message: "Message",
      messagePlaceholder: "Your message...",
      send: "Send",
      sending: "Sending...",
      thankYou: "Thank you!",
      messageSent: "Your message has been sent. We will get back to you as soon as possible.",
      backToDashboard: "Back to dashboard"
    },

    privacy: {
      title: "Privacy Policy",
      lastUpdate: "Last update",
      intro: "At Spirify, we attach great importance to protecting your personal data. This privacy policy explains what information we collect, how we use it and what your rights are.",
      dataCollected: "Data collected",
      dataCollectedText: "We collect the following data:",
      data1: "Account information: username, email address",
      data2: "Progress data: completed lessons, scores, XP",
      data3: "Technical data: browser type, IP address (anonymized)",
      dataUsage: "Data usage",
      dataUsageText: "Your data is used to:",
      usage1: "Provide and improve our services",
      usage2: "Personalize your learning experience",
      usage3: "Send you important notifications",
      usage4: "Compile anonymized statistics",
      dataProtection: "Data protection",
      dataProtectionText: "We implement appropriate technical and organizational security measures to protect your data against unauthorized access, modification, disclosure or destruction. Your data is stored on secure and encrypted servers.",
      yourRights: "Your rights",
      yourRightsText: "In accordance with GDPR, you have the following rights:",
      right1: "Right to access your data",
      right2: "Right to rectification",
      right3: "Right to erasure (right to be forgotten)",
      right4: "Right to data portability",
      right5: "Right to object to processing",
      cookies: "Cookies",
      cookiesText: "We use essential cookies for the site to function (authentication, language preferences). No advertising or tracking cookies are used.",
      contact: "Contact",
      contactText: "To exercise your rights or for any questions regarding your personal data, contact us:",
      back: "Back"
    },

    legal: {
      title: "Legal mentions",
      editor: "Site editor",
      hosting: "Hosting",
      intellectualProperty: "Intellectual property",
      intellectualPropertyText: "This entire site is subject to French and international legislation on copyright and intellectual property. All reproduction rights are reserved, including downloadable documents and iconographic and photographic representations.",
      personalData: "Personal data",
      personalDataText: "In accordance with the General Data Protection Regulation (GDPR), you have the right to access, rectify and delete data concerning you. To exercise this right, contact us at: contact@spirify.ai",
      cookies: "Cookies",
      cookiesText: "This site uses cookies to improve user experience and compile visit statistics. By continuing to browse this site, you accept the use of cookies.",
      liability: "Liability",
      liabilityText: "The information provided on this site is for informational purposes only. MFP Services France cannot guarantee the accuracy, completeness or timeliness of the information published on the site.",
      lastUpdate: "Last update"
    }
  },

  it: {
    auth: {
      login: "Accedi",
      signup: "Iscriviti",
      email: "Email",
      password: "Password",
      username: "Nome utente",
      forgotPassword: "Password dimenticata?",
      continueWithGoogle: "Continua con Google",
      noAccount: "Non hai ancora un account?",
      haveAccount: "Hai già un account?",
      loginButton: "Connessione",
      signupButton: "Iscrizione",
      or: "o",
      welcomeBack: "Bentornato!",
      loginSubtitle: "Accedi per continuare il tuo apprendimento",
      loggingIn: "Connessione in corso...",
      createAccount: "Crea un account gratuitamente",
      emailPlaceholder: "tu@esempio.com",
      errorInvalid: "Email o password errati",
      signupTitle: "Inizia la tua avventura",
      signupSubtitle: "Crea il tuo account gratuito e scopri le religioni",
      confirmPassword: "Conferma password",
      creating: "Creazione in corso...",
      errorPasswordLength: "La password deve contenere almeno 6 caratteri",
      errorPasswordMatch: "Le password non corrispondono",
      errorUsernameLength: "Il nome utente deve contenere almeno 3 caratteri",
      errorSignup: "Errore durante la creazione dell'account",
      passwordStrength: "Forza della password",
      weak: "Debole",
      medium: "Media",
      strong: "Forte",
      termsPrefix: "Creando un account, accetti i nostri",
      termsLink: "termini di utilizzo",
      alreadyHaveAccount: "Hai già un account?",
      backToHome: "← Torna alla home"
    },

    // Navigation
    nav: {
      audience: "A chi si rivolge",
      howItWorks: "Come funziona",
      paths: "Percorsi",
      faq: "FAQ",
      pricing: "Prezzi",
      startFree: "Inizia gratis",
      myDashboard: "La mia Dashboard"
    },
    
    // Hero Section
    hero: {
      badge: "Un nuovo modo di imparare le religioni",
      title: "Scopri le religioni in",
      titleHighlight: "5 minuti al giorno",
      description: "Spirify è una piattaforma ludica e rispettosa per scoprire le religioni, condividere l'apprendimento in famiglia e praticare le lingue attraverso contenuti culturali, senza proselitismo.",
      subtitle: "Spirify è una piattaforma ludica e rispettosa per scoprire le religioni, condividere l'apprendimento in famiglia e praticare le lingue attraverso contenuti culturali, senza proselitismo.",
      feature1: "Micro-lezioni di 5-10 minuti",
      feature2: "5+ religioni e spiritualità",
      feature3: "Approccio comparativo e rispettoso",
      feature4: "Gamification (XP, livelli, serie)",
      cta: "Inizia gratis",
      ctaSecondary: "Vedi come funziona",
      noCommitment: "Nessun impegno, accesso immediato alla versione di scoperta."
    },

    // Audience Section
    audience: {
      title: "A chi si rivolge Spirify?",
      subtitle: "Spirify si rivolge a chiunque voglia comprendere le religioni senza entrare in dibattiti teologici complessi. Non è necessario essere credenti o specialisti: basta essere curiosi.",
      card1Title: "Curiosi e cercatori di senso",
      card1Text: "Vuoi capire meglio le religioni di cui si parla nei notiziari, in famiglia o nella società, senza avere tempo di leggere centinaia di pagine.",
      card2Title: "Credenti che vogliono approfondire",
      card2Text: "Pratichi già una religione ma vuoi capire meglio la sua storia, i suoi testi, le sue pratiche – e scoprire come funzionano le altre tradizioni.",
      card3Title: "Studenti, educatori e mediatori",
      card3Text: "Lavori con giovani, pubblici diversi o in contesti interculturali, e hai bisogno di uno strumento semplice, neutrale e pedagogico per parlare di religione."
    },

    // How It Works
    howItWorks: {
      title: "Come funziona?",
      subtitle: "L'esperienza è pensata come un gioco educativo: scegli una religione o un percorso trasversale, poi progredisci sbloccando livelli, lezioni e badge.",
      step1Title: "Scegli il tuo percorso",
      step1Text: "Islam, cristianesimo, ebraismo o \"Spiritualità e dialogo\": seleziona il percorso che ti interessa di più, o combinali.",
      step2Title: "Segui micro-lezioni quotidiane",
      step2Text: "Ogni lezione dura tra 5 e 10 minuti, con un mix di testi semplici, quiz, domande a risposta multipla ed esempi concreti.",
      step3Title: "Progredisci e sblocca badge",
      step3Text: "Guadagni punti esperienza (XP), sblocchi nuovi capitoli e segui i tuoi progressi giorno dopo giorno.",
      videoBadge: "Vedi in azione",
      videoTitle: "Scopri Spirify in 2 minuti",
      videoSubtitle: "Sfoglia una lezione completa e vedi come l'apprendimento diventa un gioco accattivante",
      benefit1: "5 min al giorno",
      benefit1Sub: "Impara al tuo ritmo, senza pressione",
      benefit2: "Gamification",
      benefit2Sub: "XP, badge e classifiche motivanti",
      benefit3: "Progressi chiari",
      benefit3Sub: "Segui i tuoi progressi in tempo reale",
      demoTitle: "Scopri la nostra piattaforma interattiva",
      demoSubtitle: "Impara al tuo ritmo con lezioni gamificate, quiz interattivi e monitora i tuoi progressi in tempo reale.",
      startFree: "Inizia gratuitamente"
    },

    // Paths
    paths: {
      title: "Percorsi strutturati per ogni religione",
      subtitle: "Ogni tradizione è presentata attraverso moduli chiari: basi, testi, riti, festività, storia e questioni contemporanee.",
      badge: "Percorso",
      islam: "Islam",
      islamDesc: "Basi della fede, Corano e sure, preghiera (salat), digiuno, festività, storia e diversità delle pratiche musulmane.",
      islamItem1: "Basi della fede e dei pilastri",
      islamItem2: "Struttura del Corano e sure",
      islamItem3: "Preghiera, digiuno, festività",
      islamItem4: "Storia e diversità contemporanea",
      christianity: "Cristianesimo",
      christianityDesc: "Bibbia e Vangeli, principali rami, preghiera, sacramenti, festività cristiane, storia e questioni attuali.",
      christianityItem1: "Bibbia, Nuovo Testamento, Vangeli",
      christianityItem2: "Rami e tradizioni",
      christianityItem3: "Preghiera, sacramenti, festività",
      christianityItem4: "Storia e mondo contemporaneo",
      judaism: "Ebraismo",
      judaismDesc: "Torah, Tanakh, festività ebraiche, Shabbat, pratiche comunitarie, storia del popolo ebraico.",
      judaismItem1: "Testi: Torah e Tanakh",
      judaismItem2: "Shabbat e vita comunitaria",
      judaismItem3: "Festività e calendario",
      judaismItem4: "Grandi tappe storiche",
      spirituality: "Spiritualità e dialogo",
      spiritualityDesc: "Valori comuni, etica, compassione, giustizia, interiorità e moduli comparativi.",
      spiritualityItem1: "Valori ed etica",
      spiritualityItem2: "Preghiera e interiorità",
      spiritualityItem3: "Confronti delle pratiche",
      spiritualityItem4: "Dialogo e comprensione reciproca",
      buddhism: "Buddismo",
      buddhismDesc: "Le 4 Nobili Verità, il Nobile Ottuplice Sentiero, meditazione, karma, Nirvana e tradizioni buddiste.",
      buddhismItem1: "Le 4 Nobili Verità",
      buddhismItem2: "Il Nobile Ottuplice Sentiero",
      buddhismItem3: "Meditazione e pratica",
      buddhismItem4: "Scuole e tradizioni",
      hinduism: "Induismo",
      hinduismDesc: "Veda, Upanishad, karma, dharma, yoga, divinità e pratiche spirituali indù.",
      hinduismItem1: "Testi sacri e Veda",
      hinduismItem2: "Karma, dharma e moksha",
      hinduismItem3: "Yoga e meditazione",
      hinduismItem4: "Divinità e rituali",
      taoism: "Taoismo",
      taoismDesc: "Il Tao, Wu Wei, Yin-Yang, Zhuangzi, armonia con la natura e saggezza orientale.",
      taoismItem1: "Il Tao Te Ching",
      taoismItem2: "Wu Wei e non-azione",
      taoismItem3: "Yin e Yang",
      taoismItem4: "Saggezza e armonia",
      transversal: "Trasversale",
      transversalDesc: "Percorsi comparativi che collegano le tradizioni: non-dualità, sofferenza, azione giusta.",
      transversalItem1: "Non-dualità e illusione",
      transversalItem2: "Sofferenza e liberazione",
      transversalItem3: "Azione giusta",
      transversalItem4: "Ponti tra tradizioni",
      newBadge: "Nuovo",
      approach: "Ogni percorso è progettato con un approccio fattuale e rispettoso, senza giudizio né presa di posizione dottrinale."
    },

    // Features
    features: {
      title: "Una pedagogia semplice e gamificata",
      subtitle: "L'obiettivo è rendere l'apprendimento delle religioni fluido e motivante come l'apprendimento di una lingua.",
      microLessons: "Micro-lezioni",
      microLessonsText: "Lezioni molto brevi per progredire anche con un'agenda fitta.",
      quizzes: "Quiz interattivi",
      quizzesText: "Domande a risposta multipla, vero/falso, associazioni di concetti per verificare la comprensione.",
      comparative: "Approccio comparativo",
      comparativeText: "Moduli che mostrano i punti comuni e le differenze tra islam, cristianesimo ed ebraismo.",
      gamification: "Gamification",
      gamificationText: "Punti esperienza, livelli, serie di giorni consecutivi, badge tematici.",
      progress: "Monitoraggio progressi",
      progressText: "Dashboard semplice con progressi per percorso, numero di lezioni completate.",
      revision: "Modalità \"revisione\"",
      revisionText: "Possibilità di ripetere rapidamente i quiz per rivedere i concetti chiave."
    },

    // FAQ
    faq: {
      title: "Domande frequenti",
      subtitle: "Alcune risposte alle domande più comuni su Spirify.",
      q1: "Spirify promuove una religione?",
      a1: "No. Spirify è una piattaforma educativa. Presenta le religioni studiate in modo fattuale e rispettoso, senza proselitismo o giudizio.",
      q2: "Devo essere credente per usare Spirify?",
      a2: "No. La piattaforma è per credenti e non credenti, curiosi o in cerca di comprensione.",
      q3: "Chi crea i contenuti?",
      a3: "Il contenuto è sviluppato da fonti riconosciute e può essere rivisto da persone formate negli studi religiosi per garantire una presentazione equilibrata.",
      q4: "Le sette religioni sono trattate allo stesso modo?",
      a4: "Sì. Ogni religione (Islam, Cristianesimo, Ebraismo, Buddismo, Induismo, Taoismo, Spiritualità) ha il proprio percorso strutturato allo stesso modo, con lo stesso numero di lezioni ed esercizi.",
      q5: "Ci saranno altre religioni in futuro?",
      a5: "Sì! Arricchiamo continuamente la nostra piattaforma. Recentemente abbiamo aggiunto Buddismo, Induismo e Taoismo con 20 lezioni ciascuno, inclusi livelli Esperti. Altre religioni e tradizioni spirituali potranno essere aggiunte in base alla domanda.",
      q6: "Come posso essere informato del lancio?",
      a6: "Puoi creare un account gratuito subito. Sarai informato delle novità via email.",
      q7: "Posso creare profili per la mia famiglia?",
      a7: "Sì! Con un account Premium, puoi creare fino a 4 profili familiari (1 principale + 3 membri). Ogni profilo ha i propri progressi, XP e serie. Perfetto per imparare insieme mantenendo una competizione amichevole!",
      q8: "In quali lingue posso studiare su Spirify?",
      a8: "Spirify è disponibile in 6 lingue: Français, English, Español, Italiano, Português e العربية. Tutti i contenuti (lezioni, domande, spiegazioni) sono completamente tradotti. Puoi cambiare lingua in qualsiasi momento nell'app."
    },

    // CTA
    cta: {
      title: "Pronto per iniziare il tuo viaggio di scoperta?",
      subtitle: "Unisciti a migliaia di studenti che scoprono le religioni in modo ludico, rispettoso e fattuale.",
      button: "Inizia ora",
      free: "Gratis - Nessuna carta di credito richiesta"
    },

    // Footer
    footer: {
      copyright: "© 2024 Spirify – Piattaforma di apprendimento neutrale delle religioni e della spiritualità.",
      contact: "Contatto",
      privacy: "Privacy",
      legal: "Note legali",
      rights: "Tutti i diritti riservati."
    },

    // Testimonials
    testimonials: {
      title: "Cosa dicono di Spirify",
      subtitle: "Migliaia di utenti scoprono le religioni con Spirify",
      badge: "Testimonianze",
      testimonial1Text: "Un approccio neutrale e pedagogico che mi ha permesso di comprendere meglio l'Islam. Le lezioni sono brevi e molto ben strutturate.",
      testimonial1Name: "Marie L.",
      testimonial1Role: "Insegnante",
      testimonial2Text: "Come musulmano, ho apprezzato riscoprire la mia religione ed esplorare il cristianesimo e l'ebraismo. Spirify favorisce davvero il dialogo.",
      testimonial2Name: "Ahmed K.",
      testimonial2Role: "Studente",
      testimonial3Text: "Volevo capire le religioni senza sentirmi giudicata. Spirify è perfetto per questo: neutrale, rispettoso e ludico!",
      testimonial3Name: "Sophie B.",
      testimonial3Role: "Designer",
      testimonial4Text: "Perfetto per i miei corsi di storia delle religioni. I miei studenti adorano l'approccio ludico e oggettivo di Spirify.",
      testimonial4Name: "Jean P.",
      testimonial4Role: "Professore di storia",
      testimonial5Text: "Come ebrea, ho potuto comprendere meglio la storia della mia religione e scoprire le altre tradizioni abramitiche. Una vera ricchezza!",
      testimonial5Name: "Rachel M.",
      testimonial5Role: "Medico",
      testimonial6Text: "App geniale per comprendere il mondo che ci circonda. Le lezioni sono brevi, perfette per il mio programma impegnativo.",
      testimonial6Name: "Thomas D.",
      testimonial6Role: "Ingegnere",
      stat1: "Utenti attivi",
      stat2: "Lezioni disponibili",
      stat3: "Valutazione media",
      stat4: "Religioni & Spiritualità"
    },

    // Demo
    demo: {
      live: "IN DIRETTA",
      play: "Play",
      pause: "Pausa",
      step1: "Passo 1",
      step2: "Passo 2",
      step3: "Passo 3",
      step4: "Passo 4",
      step5: "Passo 5",
      welcome: "Benvenuto su Spirify",
      choosePathSubtitle: "Scegli il tuo percorso di apprendimento",
      chooseYourPath: "Scegli il Tuo Percorso",
      islamChristianismJudaism: "Islam, Cristianesimo o Ebraismo",
      islamCourse: "Percorso Islam",
      fivePillars: "I 5 Pilastri dell'Islam",
      startFirstLesson: "Inizia la tua prima lezione",
      shahada: "La Shahada - Professione di fede",
      discoverFirstPillar: "Scopri il primo pilastro dell'Islam",
      lessons: "lezioni",
      exercises: "esercizi",
      minutes: "min",
      start: "Inizia",
      interactiveQuestion: "Domanda Interattiva",
      testKnowledge: "Metti alla prova le tue conoscenze",
      hearts: "Cuori",
      whatMeansShahada: "Cosa significa \"Shahada\"?",
      ramadanFasting: "Il digiuno del Ramadan",
      professionOfFaith: "La professione di fede musulmana",
      dailyPrayer: "La preghiera quotidiana",
      pilgrimageToMecca: "Il pellegrinaggio alla Mecca",
      congrats: "Bravo! Risposta Corretta",
      correctAnswer: "Ottima risposta! La Shahada è infatti la professione di fede, primo pilastro dell'Islam.",
      gained: "Guadagnato",
      yourProgress: "Segui i Tuoi Progressi",
      statsRealTime: "Statistiche e classifica in tempo reale",
      activeStreak: "Serie attiva",
      days: "giorni",
      totalEarned: "Totale guadagnato",
      ranking: "Classifica",
      continueLearning: "Continua ad imparare",
      unlockMore: "Continua per sbloccare più contenuti"
    },

    // Dashboard
    dashboard: {
      welcome: "Benvenuto su Spirify!",
      selectReligion: "Scegli una religione",
      selectReligionDesc: "Seleziona il percorso che desideri iniziare",
      continue: "Continua",
      progress: "Progresso",
      lessonsCompleted: "lezioni completate",
      currentStreak: "Serie attuale",
      days: "giorni",
      totalXP: "XP Totale",
      level: "Livello",
      islam: "Islam",
      christianity: "Cristianesimo",
      judaism: "Ebraismo",
      spirituality: "Spiritualità",
      lessons: "lezioni",
      completed: "completato",
      locked: "bloccato",
      start: "Inizia",
      resume: "Riprendi",
      yourProgress: "I tuoi progressi",
      xp: "XP",
      hearts: "Cuori",
      logout: "Disconnetti",
      chooseYourPath: "Scegli il tuo percorso",
      xpEarned: "XP guadagnati",
      streak: "giorni consecutivi",
      unlockAfter: "Sblocca dopo",
      todaysGoal: "Obiettivo del giorno",
      heartsExplanation: "Perdi 1 cuore per ogni errore. Più cuori = continua a imparare!",
      leaderboard: "Classifica",
      gamification: "Progresso",
      badges: "Badge",
      goals: "Obiettivi",
      settings: "Impostazioni",
      questionPrompt: "Quale religione vorresti scoprire oggi?",
      clickToStart: "Clicca per iniziare",
      questions: "Domande",
      // Course descriptions
      islamName: "Islam",
      islamDesc: "Scopri i fondamenti della fede musulmana, il Corano, le pratiche e la storia",
      christianismName: "Cristianesimo",
      christianismDesc: "Esplora la Bibbia, i Vangeli, i sacramenti e le tradizioni cristiane",
      judaismName: "Ebraismo",
      judaismDesc: "Comprendi la Torah, le festività ebraiche, lo Shabbat e la storia del popolo ebraico",
      spiritualityName: "Spiritualità e Dialogo",
      spiritualityDesc: "Scopri i punti in comune, i valori universali e l'approccio comparativo",
      bouddhismeName: "Buddismo",
      bouddhismeDesc: "Scopri gli insegnamenti del Buddha, la meditazione, le Quattro Nobili Verità e il cammino verso l'illuminazione",
      hindouismeName: "Induismo",
      hindouismeDesc: "Esplora i Veda, il karma, il dharma, le grandi divinità e le vie dello yoga verso la liberazione",
      taoismeName: "Taoismo",
      taoismeDesc: "Immergiti nella saggezza del Tao, il Wu Wei, l'armonia Yin-Yang e la filosofia di Lao Tzu",
      transversalName: "Trasversale",
      transversalDesc: "Percorsi comparativi e tematici che collegano le tradizioni e approfondiscono i grandi temi universali",
      loadingLessons: "Caricamento lezioni...",
      noLessons: "Nessuna lezione disponibile",
      selectCourse: "Seleziona un percorso",
      selectCourseDesc: "Scegli la religione che desideri scoprire",
      startLesson: "Inizia",
      unlockAfter: "Sblocca dopo",
      xpEarned: "XP guadagnati",
      streak: "giorni consecutivi",
      todaysGoal: "Obiettivo del giorno",
      leaderboard: "Classifica",
      gamification: "Progresso",
      badges: "Badge",
      goals: "Obiettivi",
      continueLesson: "Continua la lezione"
    },

    // Settings
    settings: {
      title: "Impostazioni",
      profile: "Profilo",
      username: "Nome utente",
      email: "Email",
      status: "Stato",
      premium: "Premium",
      free: "Gratuito",
      language: "Lingua",
      languageDescription: "Scegli la tua lingua preferita per l'interfaccia",
      notifications: "Notifiche",
      notificationsDescription: "Le impostazioni delle notifiche saranno presto disponibili.",
      privacy: "Privacy",
      privacyPolicy: "Politica sulla privacy",
      termsOfService: "Termini di servizio",
      backToDashboard: "Torna alla dashboard",
      loading: "Caricamento..."
    },

    // Lesson
    lesson: {
      question: "Domanda",
      lessonProgress: "Lezione",
      loading: "Caricamento della lezione...",
      checkAnswer: "Verifica",
      continue: "Continua",
      continueLabel: "Continua",
      nextQuestion: "Prossima domanda",
      finish: "Termina la lezione",
      correct: "Eccellente!",
      incorrect: "Non proprio",
      explanation: "Spiegazione",
      correctAnswer: "La risposta corretta era:",
      heartLost: "Hai perso 1 cuore. Te ne restano",
      heart: "cuore",
      hearts: "cuori",
      xpGained: "XP guadagnati",
      reviewMode: "Modalità Ripasso",
      reviewIntro: "Hai fatto qualche errore. Niente panico! Ora ripasserai solo le domande sbagliate.",
      questionsToReview: "domande da ripassare",
      reviewExplanation: "Rispondi correttamente per convalidare la lezione!",
      heartsRemaining: "Cuori rimanenti:",
      startReview: "Inizia il ripasso",
      noHearts: "Nessun cuore rimasto!",
      noHeartsMessage: "Hai perso tutti i tuoi cuori. Devi ricominciare la lezione dall'inizio.",
      tip: "Consiglio",
      tipMessage: "Prenditi il tempo di leggere bene le spiegazioni dopo ogni errore!",
      restart: "Ricomincia la lezione",
      backToDashboard: "Torna alla dashboard",
      correctAnswers: "corrette",
      wellDone: "Bravo!",
      lessonCompleted: "Lezione completata",
      heartsLeft: "Cuori rimasti",
      learnMore: "Per approfondire",
      nextLesson: "Lezione successiva",
      // Duolingo style micro-challenges
      yourTurn: "Tocca a te",
      congrats: "Bravissimo!",
      oops: "Ops!",
      tryAgain: "Non è corretto, ma imparerai!",
      didYouKnow: "Lo sapevi?",
      theCorrectAnswer: "La risposta corretta era:",
      perfectStreak: "Serie perfetta",
      finishReview: "Termina il ripasso",
      // Mascot & completion screen messages
      mascotCelebration: "INCREDIBILE!",
      mascotCelebrationSub: "Sei un campione!",
      mascotHappy: "BRAVO!",
      mascotHappySub: "Ottimo lavoro!",
      mascotEncouraging: "BEN FATTO!",
      mascotEncouragingSub: "Continua così!",
      mascotSupportive: "BUON SFORZO!",
      mascotSupportiveSub: "Ce la farai!",
      mascotContinue: "Continua",
      perfect: "Perfetto!",
      perfectSub: "Hai risposto tutto correttamente!",
      excellent: "Eccellente!",
      excellentSub: "Hai davvero padroneggiato questo argomento!",
      goodJob: "Ben fatto!",
      goodJobSub: "Continua così!",
      notBad: "Non male!",
      notBadSub: "Stai migliorando, continua!",
      keepGoing: "Continua!",
      keepGoingSub: "La pratica rende perfetti!",
      accuracy: "Precisione",
      // Combo messages
      combo3: "In fiamme!",
      combo5: "Incredibile!",
      combo7: "Superstar!",
      combo10: "Leggendario!",
      comboPerfect: "Perfetto!",
      comboStreak: "{streak} risposte corrette di fila!",
      comboPerfectSub: "100% di successo!",
      skipReview: "Salta e vedi il riepilogo",
      finishReview: "Finisci il ripasso"
    },

    // Family
    family: {
      title: "Famiglia",
      subtitle: "Imparate insieme, al vostro ritmo.",
      members: "Membri",
      rankings: "Classifica",
      badges: "Badge",
      groupTitle: "Gruppo famiglia",
      groupSubtitle: "Fino a 4 profili sullo stesso account.",
      addProfile: "Aggiungi profilo",
      createProfile: "Crea profilo",
      inviteFamily: "Invita la tua famiglia",
      inviteMessage: "Crea una competizione amichevole per imparare più regolarmente.",
      familyRankings: "Classifica famiglia",
      rankingsObjective: "Obiettivo: progredire insieme, senza pressione.",
      familyBadges: "Badge famiglia",
      badgesMessage: "Ricompense per la costanza e l'impegno.",
      familyStats: "Statistiche famiglia",
      weeklyXP: "XP questa settimana",
      monthlyXP: "XP questo mese",
      streak: "Serie",
      week: "Settimana",
      month: "Mese",
      pseudo: "Soprannome",
      pseudoPlaceholder: "Es: Mamma, Papà, Luca...",
      avatar: "Avatar",
      main: "Principale",
      earned: "Ottenuto",
      toUnlock: "Da sbloccare",
      totalMembers: "Membri",
      totalXP: "XP totali",
      newProfile: "Nuovo profilo",
      cancel: "Annulla",
      create: "Crea",
      delete: "Elimina",
      deleteConfirm: "Eliminare questo profilo? Questa azione è irreversibile.",
      profileCreated: "Profilo creato!",
      profileDeleted: "Profilo eliminato",
      profilesLabel: "profili",
      days: "giorni",
      daysShort: "g",
      you: "tu",
      pseudoRequired: "Il soprannome è obbligatorio",
      active: "Attivo",
      switchedTo: "Profilo attivo",
      whoIsLearning: "Chi studia oggi?",
      clickToSwitch: "Clicca per attivare questo profilo"
    },

    // Leaderboard
    leaderboard: {
      title: "Classifica",
      subtitle: "Progredisci ogni giorno e scala la classifica.",
      yourRank: "La tua posizione",
      topPlayers: "Top studenti",
      weekly: "Settimana",
      monthly: "Mese",
      allTime: "Generale",
      annualChallenge: "Sfida annuale",
      annualDescription: "Ogni anno, i primi 3 della classifica generale vengono premiati.",
      close: "Chiudi"
    },

    // Pricing
    pricing: {
      title: "Scegli la tua formula",
      subtitle: "Un'esperienza ludica, culturale e multilingue per tutte le età.",
      badge: "Prezzi semplici e trasparenti",
      free: "Gratuito",
      premium: "Premium",
      monthly: "Mensile",
      annual: "Annuale",
      perMonth: "al mese",
      perYear: "all'anno",
      forever: "per sempre",
      subscribe: "Iscriviti",
      features: "Funzionalità",
      mostPopular: "Il più popolare",
      startFree: "Inizia gratis",
      goPremium: "Sblocca l'esperienza completa",
      savePercent: "Risparmia il 33%",
      annualPrice: "39,99€/anno",
      freeDescription: "Scoperta completa per iniziare.",
      lessonsCount: "24 lezioni (6 per corso)",
      interactiveQuestions: "Domande interattive",
      basicGamification: "Gamification di base",
      heartsPerDay: "5 cuori al giorno",
      publicLeaderboard: "Classifica pubblica",
      premiumDescription: "Sblocca l'esperienza completa: apprendimento + lingue + competizione.",
      premiumSubtitle: "Fino a 4 profili inclusi — ideale per le famiglie.",
      everythingFree: "Tutto del piano Gratuito",
      familyMode: "Modalità Famiglia (fino a 4 profili)",
      allLanguages: "Accesso a tutte le lingue",
      unlimitedAccess: "Accesso illimitato a tutti i percorsi",
      annualChallenges: "Classifiche & sfide annuali",
      unlimitedHearts: "Cuori illimitati",
      downloadCertificates: "Certificati scaricabili",
      offlineMode: "Modalità offline",
      prioritySupport: "Supporto prioritario",
      familyTip: "Consiglio: lancia una competizione amichevole in famiglia con la classifica interna (settimana / mese / streak).",
      lockedFamilyMode: "Modalità Famiglia (fino a 4 profili)",
      lockedAllLanguages: "Accesso a tutte le lingue",
      lockedChallenges: "Sfide annuali ed esclusive",
      lockedAdvancedLevels: "Livelli avanzati",
      lockedOfflineMode: "Modalità offline",
      footerNote: "Spirify è un'esperienza ludica e rispettosa. Nessun proselitismo.",
      ctaTitle: "Pronto a iniziare il tuo viaggio spirituale?",
      ctaSubtitle: "Unisciti a migliaia di studenti che scoprono le religioni in modo divertente e rispettoso.",
      noCreditCard: "Nessuna carta di credito richiesta",
      redirecting: "Reindirizzamento al pagamento...",
      login: "Accedi"
    }
  },

  es: {
    auth: {
      login: "Iniciar sesión",
      signup: "Registrarse",
      email: "Correo electrónico",
      password: "Contraseña",
      username: "Nombre de usuario",
      forgotPassword: "¿Olvidaste tu contraseña?",
      continueWithGoogle: "Continuar con Google",
      noAccount: "¿Aún no tienes una cuenta?",
      haveAccount: "¿Ya tienes una cuenta?",
      loginButton: "Iniciar sesión",
      signupButton: "Registrarse",
      or: "o",
      welcomeBack: "¡Bienvenido de nuevo!",
      loginSubtitle: "Inicia sesión para continuar con tu aprendizaje",
      loggingIn: "Iniciando sesión...",
      createAccount: "Crea una cuenta gratis",
      emailPlaceholder: "tú@ejemplo.com",
      errorInvalid: "Correo electrónico o contraseña incorrecta",
      signupTitle: "Comienza tu aventura",
      signupSubtitle: "Crea tu cuenta gratuita y descubre las religiones",
      confirmPassword: "Confirmar contraseña",
      creating: "Creando...",
      errorPasswordLength: "La contraseña debe tener al menos 6 caracteres",
      errorPasswordMatch: "Las contraseñas no coinciden",
      errorUsernameLength: "El nombre de usuario debe tener al menos 3 caracteres",
      errorSignup: "Error al crear la cuenta",
      passwordStrength: "Fortaleza de la contraseña",
      weak: "Débil",
      medium: "Medio",
      strong: "Fuerte",
      termsPrefix: "Al crear una cuenta, aceptas nuestros",
      termsLink: "términos de uso",
      alreadyHaveAccount: "¿Ya tienes una cuenta?",
      backToHome: "← Volver al inicio"
    },

    // Navigation
    nav: {
      audience: "¿Para quién?",
      howItWorks: "Cómo funciona",
      paths: "Rutas",
      faq: "FAQ",
      pricing: "Precios",
      startFree: "Empezar gratis",
      myDashboard: "Mi Panel"
    },
    
    // Hero Section
    hero: {
      badge: "Una nueva forma de aprender sobre religiones",
      title: "Descubre las religiones en",
      titleHighlight: "5 minutos al día",
      description: "Spirify es una plataforma lúdica y respetuosa para descubrir las religiones, compartir el aprendizaje en familia y practicar idiomas a través de contenidos culturales, sin proselitismo.",
      subtitle: "Spirify es una plataforma lúdica y respetuosa para descubrir las religiones, compartir el aprendizaje en familia y practicar idiomas a través de contenidos culturales, sin proselitismo.",
      feature1: "Micro-lecciones de 5-10 minutos",
      feature2: "5+ religiones y espiritualidades",
      feature3: "Enfoque comparativo y respetuoso",
      feature4: "Gamificación (XP, niveles, rachas)",
      cta: "Empezar gratis",
      ctaSecondary: "Ver cómo funciona",
      noCommitment: "Sin compromiso, acceso inmediato a la versión de descubrimiento."
    },

    // Audience Section
    audience: {
      title: "¿Para quién es Spirify?",
      subtitle: "Spirify es para cualquiera que quiera entender las religiones sin entrar en debates teológicos complejos. No necesitas ser creyente ni especialista: solo necesitas ser curioso.",
      card1Title: "Curiosos y buscadores",
      card1Text: "Quieres entender mejor las religiones de las que oímos hablar en las noticias, en tu familia o en la sociedad, sin tener tiempo de leer cientos de páginas.",
      card2Title: "Creyentes que quieren profundizar",
      card2Text: "Ya practicas una religión pero quieres entender mejor su historia, textos, prácticas – y descubrir cómo funcionan otras tradiciones.",
      card3Title: "Estudiantes, educadores y mediadores",
      card3Text: "Trabajas con jóvenes, audiencias diversas o en contextos interculturales, y necesitas una herramienta simple, neutral y educativa para hablar sobre religión."
    },

    // How It Works
    howItWorks: {
      title: "¿Cómo funciona?",
      subtitle: "La experiencia está diseñada como un juego educativo: eliges una religión o un camino transversal, luego progresas desbloqueando niveles, lecciones y insignias.",
      step1Title: "Elige tu camino",
      step1Text: "Islam, cristianismo, judaísmo o \"Espiritualidad y diálogo\": selecciona el camino que más te interese, o combínalos.",
      step2Title: "Sigue micro-lecciones diarias",
      step2Text: "Cada lección dura entre 5 y 10 minutos, con una mezcla de textos simples, cuestionarios, preguntas de opción múltiple y ejemplos concretos.",
      step3Title: "Progresa y desbloquea insignias",
      step3Text: "Ganas puntos de experiencia (XP), desbloqueas nuevos capítulos y sigues tu progreso día a día.",
      videoBadge: "Ver en acción",
      videoTitle: "Descubre Spirify en 2 minutos",
      videoSubtitle: "Explora una lección completa y ve cómo el aprendizaje se convierte en un juego cautivador",
      benefit1: "5 min al día",
      benefit1Sub: "Aprende a tu ritmo, sin presión",
      benefit2: "Gamificación",
      benefit2Sub: "XP, insignias y clasificaciones motivadoras",
      benefit3: "Progreso claro",
      benefit3Sub: "Sigue tu progreso en tiempo real",
      demoTitle: "Descubre nuestra plataforma interactiva",
      demoSubtitle: "Aprende a tu ritmo con lecciones gamificadas, cuestionarios interactivos y sigue tu progreso en tiempo real.",
      startFree: "Empezar gratis"
    },

    // Paths
    paths: {
      title: "Rutas estructuradas para cada religión",
      subtitle: "Cada tradición se presenta a través de módulos claros: fundamentos, textos, ritos, festividades, historia y problemas contemporáneos.",
      badge: "Ruta",
      islam: "Islam",
      islamDesc: "Fundamentos de la fe, Corán y suras, oración (salat), ayuno, festividades, historia y diversidad de prácticas musulmanas.",
      islamItem1: "Fundamentos de la fe y los pilares",
      islamItem2: "Estructura del Corán y suras",
      islamItem3: "Oración, ayuno, festividades",
      islamItem4: "Historia y diversidad contemporánea",
      christianity: "Cristianismo",
      christianityDesc: "Biblia y Evangelios, principales ramas, oración, sacramentos, festividades cristianas, historia y problemas actuales.",
      christianityItem1: "Biblia, Nuevo Testamento, Evangelios",
      christianityItem2: "Ramas y tradiciones",
      christianityItem3: "Oración, sacramentos, festividades",
      christianityItem4: "Historia y mundo contemporáneo",
      judaism: "Judaísmo",
      judaismDesc: "Torá, Tanaj, festividades judías, Shabat, prácticas comunitarias, historia del pueblo judío.",
      judaismItem1: "Textos: Torá y Tanaj",
      judaismItem2: "Shabat y vida comunitaria",
      judaismItem3: "Festividades y calendario",
      judaismItem4: "Grandes etapas históricas",
      spirituality: "Espiritualidad y diálogo",
      spiritualityDesc: "Valores comunes, ética, compasión, justicia, interioridad y módulos comparativos.",
      spiritualityItem1: "Valores y ética",
      spiritualityItem2: "Oración e interioridad",
      spiritualityItem3: "Comparaciones de prácticas",
      spiritualityItem4: "Diálogo y comprensión mutua",
      buddhism: "Budismo",
      buddhismDesc: "Las 4 Nobles Verdades, el Noble Óctuple Sendero, meditación, karma, Nirvana y tradiciones budistas.",
      buddhismItem1: "Las 4 Nobles Verdades",
      buddhismItem2: "El Noble Óctuple Sendero",
      buddhismItem3: "Meditación y práctica",
      buddhismItem4: "Escuelas y tradiciones",
      hinduism: "Hinduismo",
      hinduismDesc: "Vedas, Upanishads, karma, dharma, yoga, deidades y prácticas espirituales hindúes.",
      hinduismItem1: "Textos sagrados y Vedas",
      hinduismItem2: "Karma, dharma y moksha",
      hinduismItem3: "Yoga y meditación",
      hinduismItem4: "Deidades y rituales",
      taoism: "Taoísmo",
      taoismDesc: "El Tao, Wu Wei, Yin-Yang, Zhuangzi, armonía con la naturaleza y sabiduría oriental.",
      taoismItem1: "El Tao Te Ching",
      taoismItem2: "Wu Wei y no-acción",
      taoismItem3: "Yin y Yang",
      taoismItem4: "Sabiduría y armonía",
      transversal: "Transversal",
      transversalDesc: "Rutas comparativas que conectan tradiciones: no-dualidad, sufrimiento, acción justa.",
      transversalItem1: "No-dualidad e ilusión",
      transversalItem2: "Sufrimiento y liberación",
      transversalItem3: "Acción justa",
      transversalItem4: "Puentes entre tradiciones",
      newBadge: "Nuevo",
      approach: "Cada camino está diseñado con un enfoque factual y respetuoso, sin juicios ni posiciones doctrinales."
    },

    // Features
    features: {
      title: "Una pedagogía simple y gamificada",
      subtitle: "El objetivo es hacer que aprender sobre religiones sea tan fluido y motivador como aprender un idioma.",
      microLessons: "Micro-lecciones",
      microLessonsText: "Lecciones muy cortas para progresar incluso con una agenda ocupada.",
      quizzes: "Cuestionarios interactivos",
      quizzesText: "Preguntas de opción múltiple, verdadero/falso, asociaciones de conceptos para verificar la comprensión.",
      comparative: "Enfoque comparativo",
      comparativeText: "Módulos que muestran los puntos comunes y las diferencias entre el islam, el cristianismo y el judaísmo.",
      gamification: "Gamificación",
      gamificationText: "Puntos de experiencia, niveles, rachas de días consecutivos, insignias temáticas.",
      progress: "Seguimiento del progreso",
      progressText: "Panel simple con progreso por ruta, número de lecciones completadas.",
      revision: "Modo \"revisión\"",
      revisionText: "Capacidad de repetir rápidamente los cuestionarios para revisar conceptos clave."
    },

    // FAQ
    faq: {
      title: "Preguntas frecuentes",
      subtitle: "Algunas respuestas a las preguntas más comunes sobre Spirify.",
      q1: "¿Spirify promueve una religión?",
      a1: "No. Spirify es una plataforma educativa. Presenta las religiones estudiadas de manera fáctica y respetuosa, sin proselitismo ni juicio.",
      q2: "¿Necesito ser creyente para usar Spirify?",
      a2: "No. La plataforma es tanto para creyentes como para no creyentes, curiosos o en busca de comprensión.",
      q3: "¿Quién crea el contenido?",
      a3: "El contenido se desarrolla a partir de fuentes reconocidas y puede ser revisado por personas capacitadas en estudios religiosos para garantizar una presentación equilibrada.",
      q4: "¿Las siete religiones se tratan por igual?",
      a4: "Sí. Cada religión (Islam, Cristianismo, Judaísmo, Budismo, Hinduismo, Taoísmo, Espiritualidad) tiene su propio camino estructurado de la misma manera, con la misma cantidad de lecciones y ejercicios.",
      q5: "¿Habrá otras religiones en el futuro?",
      a5: "¡Sí! Enriquecemos continuamente nuestra plataforma. Recientemente agregamos Budismo, Hinduismo y Taoísmo con 20 lecciones cada uno, incluyendo niveles Expertos. Otras religiones y tradiciones espirituales podrán ser agregadas según la demanda.",
      q6: "¿Cómo puedo estar informado del lanzamiento?",
      a6: "Puedes crear una cuenta gratuita ahora mismo. Serás notificado de las novedades por correo electrónico.",
      q7: "¿Puedo crear perfiles para mi familia?",
      a7: "¡Sí! Con una cuenta Premium, puedes crear hasta 4 perfiles familiares (1 principal + 3 miembros). Cada perfil tiene su propio progreso, XP y racha. ¡Perfecto para aprender juntos manteniendo una competencia amistosa!",
      q8: "¿En qué idiomas puedo estudiar en Spirify?",
      a8: "Spirify está disponible en 6 idiomas: Français, English, Español, Italiano, Português y العربية. Todo el contenido (lecciones, preguntas, explicaciones) está completamente traducido. Puedes cambiar de idioma en cualquier momento en la app."
    },

    // CTA
    cta: {
      title: "¿Listo para comenzar tu viaje de descubrimiento?",
      subtitle: "Únete a miles de estudiantes que descubren las religiones de manera lúdica, respetuosa y fáctica.",
      button: "Empezar ahora",
      free: "Gratis - No se requiere tarjeta de credito"
    },

    // Footer
    footer: {
      copyright: "© 2024 Spirify – Plataforma de aprendizaje neutral de religiones y espiritualidad.",
      contact: "Contacto",
      privacy: "Privacidad",
      legal: "Aviso legal",
      rights: "Todos los derechos reservados."
    },

    // Testimonials
    testimonials: {
      title: "Lo que dicen de Spirify",
      subtitle: "Miles de usuarios descubren las religiones con Spirify",
      badge: "Testimonios",
      testimonial1Text: "Un enfoque neutral y pedagógico que me permitió comprender mejor el Islam. Las lecciones son cortas y muy bien estructuradas.",
      testimonial1Name: "Marie L.",
      testimonial1Role: "Profesora",
      testimonial2Text: "Como musulmán, aprecié redescubrir mi religión y explorar el cristianismo y el judaísmo. Spirify realmente favorece el diálogo.",
      testimonial2Name: "Ahmed K.",
      testimonial2Role: "Estudiante",
      testimonial3Text: "Quería entender las religiones sin sentirme juzgada. Spirify es perfecto para eso: neutral, respetuoso y lúdico!",
      testimonial3Name: "Sophie B.",
      testimonial3Role: "Diseñadora",
      testimonial4Text: "Perfecto para mis clases de historia de las religiones. Mis alumnos adoran el enfoque lúdico y objetivo de Spirify.",
      testimonial4Name: "Jean P.",
      testimonial4Role: "Profesor de historia",
      testimonial5Text: "Como judía, pude comprender mejor la historia de mi religión y descubrir las otras tradiciones abrahámicas. ¡Una verdadera riqueza!",
      testimonial5Name: "Rachel M.",
      testimonial5Role: "Médica",
      testimonial6Text: "Aplicación genial para comprender el mundo que nos rodea. Las lecciones son cortas, perfectas para mi agenda ocupada.",
      testimonial6Name: "Thomas D.",
      testimonial6Role: "Ingeniero",
      stat1: "Usuarios activos",
      stat2: "Lecciones disponibles",
      stat3: "Calificación promedio",
      stat4: "Religiones y Espiritualidades"
    },

    // Demo
    demo: {
      live: "EN VIVO",
      play: "Reproducir",
      pause: "Pausar",
      step1: "Paso 1",
      step2: "Paso 2",
      step3: "Paso 3",
      step4: "Paso 4",
      step5: "Paso 5",
      welcome: "Bienvenido a Spirify",
      choosePathSubtitle: "Elige tu camino de aprendizaje",
      chooseYourPath: "Elige Tu Camino",
      islamChristianismJudaism: "Islam, Cristianismo o Judaísmo",
      islamCourse: "Camino Islam",
      fivePillars: "Los 5 Pilares del Islam",
      startFirstLesson: "Comienza tu primera lección",
      shahada: "La Shahada - Profesión de fe",
      discoverFirstPillar: "Descubre el primer pilar del Islam",
      lessons: "lecciones",
      exercises: "ejercicios",
      minutes: "min",
      start: "Comenzar",
      interactiveQuestion: "Pregunta Interactiva",
      testKnowledge: "Pon a prueba tus conocimientos",
      hearts: "Corazones",
      whatMeansShahada: "¿Qué significa \"Shahada\"?",
      ramadanFasting: "El ayuno del Ramadán",
      professionOfFaith: "La profesión de fe musulmana",
      dailyPrayer: "La oración diaria",
      pilgrimageToMecca: "La peregrinación a La Meca",
      congrats: "¡Genial! Respuesta Correcta",
      correctAnswer: "¡Excelente respuesta! La Shahada es efectivamente la profesión de fe, primer pilar del Islam.",
      gained: "Ganado",
      yourProgress: "Sigue Tu Progreso",
      statsRealTime: "Estadísticas y clasificación en tiempo real",
      activeStreak: "Racha activa",
      days: "días",
      totalEarned: "Total ganado",
      ranking: "Clasificación",
      continueLearning: "Sigue aprendiendo",
      unlockMore: "Continúa para desbloquear más contenido"
    },

    // Dashboard
    dashboard: {
      welcome: "¡Bienvenido a Spirify!",
      selectReligion: "Elige una religión",
      selectReligionDesc: "Selecciona el camino que deseas comenzar",
      continue: "Continuar",
      progress: "Progreso",
      lessonsCompleted: "lecciones completadas",
      currentStreak: "Racha actual",
      days: "días",
      totalXP: "XP Total",
      level: "Nivel",
      islam: "Islam",
      christianity: "Cristianismo",
      judaism: "Judaísmo",
      spirituality: "Espiritualidad",
      lessons: "lecciones",
      completed: "completado",
      locked: "bloqueado",
      start: "Empezar",
      resume: "Continuar",
      yourProgress: "Tu progreso",
      xp: "XP",
      hearts: "Corazones",
      logout: "Cerrar sesión",
      loadingLessons: "Cargando lecciones...",
      noLessons: "No hay lecciones disponibles",
      selectCourse: "Selecciona un camino",
      selectCourseDesc: "Elige la religión que deseas descubrir",
      startLesson: "Empezar",
      unlockAfter: "Desbloquear después",
      xpEarned: "XP ganado",
      streak: "días consecutivos",
      todaysGoal: "Objetivo del día",
      heartsExplanation: "Pierdes 1 corazón por error. ¡Más corazones = sigue aprendiendo!",
      leaderboard: "Clasificación",
      gamification: "Progreso",
      badges: "Insignias",
      goals: "Objetivos",
      settings: "Configuración",
      continueLesson: "Continuar lección",
      chooseYourPath: "Elige tu camino",
      questionPrompt: "¿Qué religión te gustaría descubrir hoy?",
      clickToStart: "Haz clic para comenzar",
      questions: "Preguntas",
      islamName: "Islam",
      islamDesc: "Descubre los fundamentos de la fe musulmana, el Corán, las prácticas y la historia",
      christianismName: "Cristianismo",
      christianismDesc: "Explora la Biblia, los Evangelios, los sacramentos y las tradiciones cristianas",
      judaismName: "Judaísmo",
      judaismDesc: "Comprende la Torá, las fiestas judías, el Shabat y la historia del pueblo judío",
      spiritualityName: "Espiritualidad y Diálogo",
      spiritualityDesc: "Descubre los puntos en común, los valores universales y el enfoque comparativo",
      bouddhismeName: "Budismo",
      bouddhismeDesc: "Descubre las enseñanzas de Buda, la meditación, las Cuatro Nobles Verdades y el camino hacia la iluminación",
      hindouismeName: "Hinduismo",
      hindouismeDesc: "Explora los Vedas, el karma, el dharma, las grandes deidades y los caminos del yoga hacia la liberación",
      taoismeName: "Taoísmo",
      taoismeDesc: "Sumérgete en la sabiduría del Tao, el Wu Wei, la armonía Yin-Yang y la filosofía de Lao Tzu",
      transversalName: "Transversal",
      transversalDesc: "Rutas comparativas y temáticas que conectan tradiciones y profundizan en los grandes temas universales"
    },

    // Settings
    settings: {
      title: "Configuración",
      profile: "Perfil",
      username: "Nombre de usuario",
      email: "Correo electrónico",
      status: "Estado",
      premium: "Premium",
      free: "Gratis",
      language: "Idioma",
      languageDescription: "Elige tu idioma preferido para la interfaz",
      notifications: "Notificaciones",
      notificationsDescription: "Los ajustes de notificaciones estarán disponibles pronto.",
      privacy: "Privacidad",
      privacyPolicy: "Política de privacidad",
      termsOfService: "Términos de servicio",
      backToDashboard: "Volver al panel",
      loading: "Cargando..."
    },

    // Lesson
    lesson: {
      question: "Pregunta",
      lessonProgress: "Lección",
      loading: "Cargando lección...",
      checkAnswer: "Verificar",
      continue: "Continuar",
      nextQuestion: "Siguiente pregunta",
      finish: "Terminar la lección",
      correct: "¡Excelente!",
      incorrect: "No del todo",
      explanation: "Explicación",
      correctAnswer: "La respuesta correcta era:",
      heartLost: "Has perdido 1 corazón. Te quedan",
      heart: "corazón",
      hearts: "corazones",
      xpGained: "XP ganado",
      reviewMode: "Modo Revisión",
      reviewIntro: "Has cometido algunos errores. ¡No te preocupes! Ahora repasarás solo las preguntas que te equivocaste.",
      questionsToReview: "preguntas para revisar",
      reviewExplanation: "¡Responde correctamente para validar la lección!",
      heartsRemaining: "Corazones restantes:",
      startReview: "Comenzar revisión",
      noHearts: "¡Sin corazones!",
      noHeartsMessage: "Has perdido todos tus corazones. Debes reiniciar la lección desde el principio.",
      tip: "Consejo",
      tipMessage: "¡Tómate el tiempo para leer bien las explicaciones después de cada error!",
      restart: "Reiniciar lección",
      backToDashboard: "Volver al panel",
      correctAnswers: "correctas",
      wellDone: "¡Bravo!",
      lessonCompleted: "Lección completada",
      heartsLeft: "Corazones restantes",
      learnMore: "Para saber más",
      nextLesson: "Siguiente lección",
      // Duolingo style micro-challenges
      yourTurn: "Te toca",
      congrats: "¡Muy bien!",
      oops: "¡Ups!",
      tryAgain: "No es correcto, ¡pero aprenderás!",
      didYouKnow: "¿Lo sabías?",
      theCorrectAnswer: "La respuesta correcta era:",
      perfectStreak: "Racha perfecta",
      continueLabel: "Continuar",
      // Mascot & completion screen messages
      mascotCelebration: "¡INCREÍBLE!",
      mascotCelebrationSub: "¡Eres un campeón!",
      mascotHappy: "¡BIEN HECHO!",
      mascotHappySub: "¡Excelente trabajo!",
      mascotEncouraging: "¡BUEN TRABAJO!",
      mascotEncouragingSub: "¡Sigue así!",
      mascotSupportive: "¡BUEN ESFUERZO!",
      mascotSupportiveSub: "¡Lo vas a lograr!",
      mascotContinue: "Continuar",
      perfect: "¡Perfecto!",
      perfectSub: "¡Lo has acertado todo!",
      excellent: "¡Excelente!",
      excellentSub: "¡Realmente dominas este tema!",
      goodJob: "¡Bien hecho!",
      goodJobSub: "¡Sigue así!",
      notBad: "¡Nada mal!",
      notBadSub: "¡Estás progresando, sigue adelante!",
      keepGoing: "¡Continúa!",
      keepGoingSub: "¡La práctica hace al maestro!",
      accuracy: "Precisión",
      // Combo messages
      combo3: "¡En llamas!",
      combo5: "¡Increíble!",
      combo7: "¡Superstar!",
      combo10: "¡Legendario!",
      comboPerfect: "¡Perfecto!",
      comboStreak: "¡{streak} respuestas correctas seguidas!",
      comboPerfectSub: "¡100% de éxito!",
      skipReview: "Saltar y ver resumen",
      finishReview: "Terminar revisión"
    },

    // Family
    family: {
      title: "Familia",
      subtitle: "Aprendan juntos, a su ritmo.",
      members: "Miembros",
      rankings: "Clasificación",
      badges: "Insignias",
      groupTitle: "Grupo familiar",
      groupSubtitle: "Hasta 4 perfiles en la misma cuenta.",
      addProfile: "Añadir perfil",
      createProfile: "Crear perfil",
      inviteFamily: "Invita a tu familia",
      inviteMessage: "Crea una competición amistosa para aprender más regularmente.",
      familyRankings: "Clasificación familiar",
      rankingsObjective: "Objetivo: progresar juntos, sin presión.",
      familyBadges: "Insignias familiares",
      badgesMessage: "Recompensas por la constancia y el compromiso.",
      familyStats: "Estadísticas familiares",
      weeklyXP: "XP esta semana",
      monthlyXP: "XP este mes",
      streak: "Racha",
      week: "Semana",
      month: "Mes",
      pseudo: "Apodo",
      pseudoPlaceholder: "Ej: Mamá, Papá, Lucas...",
      avatar: "Avatar",
      main: "Principal",
      earned: "Obtenido",
      toUnlock: "Por desbloquear",
      totalMembers: "Miembros",
      totalXP: "XP acumulados",
      newProfile: "Nuevo perfil",
      cancel: "Cancelar",
      create: "Crear",
      delete: "Eliminar",
      deleteConfirm: "¿Eliminar este perfil? Esta acción es irreversible.",
      profileCreated: "¡Perfil creado!",
      profileDeleted: "Perfil eliminado",
      profilesLabel: "perfiles",
      days: "días",
      daysShort: "d",
      you: "tú",
      pseudoRequired: "El apodo es obligatorio",
      active: "Activo",
      switchedTo: "Perfil activo",
      whoIsLearning: "¿Quién estudia hoy?",
      clickToSwitch: "Haz clic para activar este perfil"
    },

    // Leaderboard
    leaderboard: {
      title: "Clasificación",
      subtitle: "Progresa cada día y sube en la clasificación.",
      yourRank: "Tu posición",
      topPlayers: "Top aprendices",
      weekly: "Semana",
      monthly: "Mes",
      allTime: "General",
      annualChallenge: "Desafío anual",
      annualDescription: "Cada año, los 3 primeros del ranking global son honrados.",
      close: "Cerrar"
    },

    // Pricing
    pricing: {
      title: "Aprende las religiones del mundo, juega en familia y practica idiomas.",
      subtitle: "Una experiencia lúdica, cultural y multilingüe para todas las edades.",
      badge: "Precios simples y transparentes",
      free: "Gratis",
      premium: "Premium",
      monthly: "Mensual",
      annual: "Anual",
      perMonth: "por mes",
      perYear: "por año",
      forever: "para siempre",
      subscribe: "Suscribirse",
      features: "Funcionalidades",
      mostPopular: "El más popular",
      startFree: "Comenzar gratis",
      goPremium: "Desbloquear la experiencia completa",
      savePercent: "Ahorra 33%",
      annualPrice: "39,99€/año",
      freeDescription: "Descubrimiento completo para comenzar.",
      lessonsCount: "24 lecciones (6 por curso)",
      interactiveQuestions: "Preguntas interactivas",
      basicGamification: "Gamificación básica",
      heartsPerDay: "5 corazones por día",
      publicLeaderboard: "Clasificación pública",
      premiumDescription: "Desbloquea la experiencia completa: aprendizaje + idiomas + competición.",
      premiumSubtitle: "Hasta 4 perfiles incluidos — ideal para familias.",
      everythingFree: "Todo del plan Gratis",
      familyMode: "Modo Familia (hasta 4 perfiles)",
      allLanguages: "Acceso a todos los idiomas",
      unlimitedAccess: "Acceso ilimitado a todos los cursos",
      annualChallenges: "Clasificaciones y desafíos anuales",
      unlimitedHearts: "Corazones ilimitados",
      downloadCertificates: "Certificados descargables",
      offlineMode: "Modo sin conexión",
      prioritySupport: "Soporte prioritario",
      familyTip: "Consejo: lanza una competición amistosa en familia con la clasificación interna (semana / mes / racha).",
      lockedFamilyMode: "Modo Familia (hasta 4 perfiles)",
      lockedAllLanguages: "Acceso a todos los idiomas",
      lockedChallenges: "Desafíos anuales y exclusivos",
      lockedAdvancedLevels: "Niveles avanzados",
      lockedOfflineMode: "Modo sin conexión",
      footerNote: "Spirify es una experiencia lúdica y respetuosa. Sin proselitismo.",
      ctaTitle: "¿Listo para comenzar tu viaje espiritual?",
      ctaSubtitle: "Únete a miles de aprendices que descubren las religiones de forma divertida y respetuosa.",
      noCreditCard: "No se requiere tarjeta de crédito",
      redirecting: "Redirigiendo al pago...",
      login: "Iniciar sesión"
    }
  },

  pt: {
    auth: {
      login: "Entrar",
      signup: "Cadastrar-se",
      email: "E-mail",
      password: "Senha",
      username: "Nome de usuário",
      forgotPassword: "Esqueceu sua senha?",
      continueWithGoogle: "Continuar com o Google",
      noAccount: "Ainda não tem uma conta?",
      haveAccount: "Já tem uma conta?",
      loginButton: "Conectar",
      signupButton: "Inscrição",
      or: "ou",
      welcomeBack: "Bem-vindo de volta!",
      loginSubtitle: "Conecte-se para continuar seu aprendizado",
      loggingIn: "Conectando...",
      createAccount: "Crie uma conta gratuitamente",
      emailPlaceholder: "voce@exemplo.com",
      errorInvalid: "E-mail ou senha incorretos",
      signupTitle: "Comece sua aventura",
      signupSubtitle: "Crie sua conta gratuita e descubra as religiões",
      confirmPassword: "Confirmar senha",
      creating: "Criando...",
      errorPasswordLength: "A senha deve ter pelo menos 6 caracteres",
      errorPasswordMatch: "As senhas não correspondem",
      errorUsernameLength: "O nome de usuário deve ter pelo menos 3 caracteres",
      errorSignup: "Erro ao criar a conta",
      passwordStrength: "Força da senha",
      weak: "Fraca",
      medium: "Média",
      strong: "Forte",
      termsPrefix: "Ao criar uma conta, você aceita nossos",
      termsLink: "termos de uso",
      alreadyHaveAccount: "Você já tem uma conta?",
      backToHome: "← Voltar para a página inicial"
    },

    // Navigation
    nav: {
      audience: "Para quem",
      howItWorks: "Como funciona",
      paths: "Caminhos",
      faq: "FAQ",
      pricing: "Preços",
      startFree: "Começar grátis",
      myDashboard: "Meu Painel"
    },
    
    // Hero Section
    hero: {
      badge: "Uma nova forma de aprender sobre religiões",
      title: "Descubra as religiões em",
      titleHighlight: "5 minutos por dia",
      description: "Spirify é uma plataforma lúdica e respeitosa para descobrir as religiões, compartilhar o aprendizado em família e praticar idiomas através de conteúdos culturais, sem proselitismo.",
      subtitle: "Spirify é uma plataforma lúdica e respeitosa para descobrir as religiões, compartilhar o aprendizado em família e praticar idiomas através de conteúdos culturais, sem proselitismo.",
      feature1: "Micro-lições de 5-10 minutos",
      feature2: "5+ religiões e espiritualidades",
      feature3: "Abordagem comparativa e respeitosa",
      feature4: "Gamificação (XP, níveis, sequências)",
      cta: "Começar grátis",
      ctaSecondary: "Ver como funciona",
      noCommitment: "Sem compromisso, acesso imediato à versão de descoberta."
    },

    // Audience Section
    audience: {
      title: "Para quem é Spirify?",
      subtitle: "Spirify é para qualquer pessoa que queira entender as religiões sem entrar em debates teológicos complexos. Você não precisa ser crente ou especialista: apenas precisa ser curioso.",
      card1Title: "Curiosos e buscadores",
      card1Text: "Você quer entender melhor as religiões sobre as quais ouvimos falar nas notícias, na sua família ou na sociedade, sem ter tempo de ler centenas de páginas.",
      card2Title: "Crentes que querem aprofundar",
      card2Text: "Você já pratica uma religião, mas quer entender melhor sua história, textos, práticas – e descobrir como outras tradições funcionam.",
      card3Title: "Estudantes, educadores e mediadores",
      card3Text: "Você trabalha com jovens, públicos diversos ou em contextos interculturais e precisa de uma ferramenta simples, neutra e educacional para falar sobre religião."
    },

    // How It Works
    howItWorks: {
      title: "Como funciona?",
      subtitle: "A experiência é projetada como um jogo educacional: você escolhe uma religião ou um caminho transversal, depois progride desbloqueando níveis, lições e distintivos.",
      step1Title: "Escolha seu caminho",
      step1Text: "Islamismo, cristianismo, judaísmo ou \"Espiritualidade e diálogo\": selecione o caminho que mais lhe interessa, ou combine-os.",
      step2Title: "Siga micro-lições diárias",
      step2Text: "Cada lição dura entre 5 e 10 minutos, com uma mistura de textos simples, questionários, perguntas de múltipla escolha e exemplos concretos.",
      step3Title: "Progrida e desbloqueie distintivos",
      step3Text: "Você ganha pontos de experiência (XP), desbloqueia novos capítulos e acompanha seu progresso dia a dia.",
      videoBadge: "Ver em ação",
      videoTitle: "Descubra Spirify em 2 minutos",
      videoSubtitle: "Navegue por uma lição completa e veja como o aprendizado se torna um jogo cativante",
      benefit1: "5 min por dia",
      benefit1Sub: "Aprenda no seu ritmo, sem pressão",
      benefit2: "Gamificação",
      benefit2Sub: "XP, distintivos e classificações motivadoras",
      benefit3: "Progresso claro",
      benefit3Sub: "Acompanhe seu progresso em tempo real",
      demoTitle: "Descubra nossa plataforma interativa",
      demoSubtitle: "Aprenda no seu ritmo com lições gamificadas, quizzes interativos e acompanhe seu progresso em tempo real.",
      startFree: "Começar gratuitamente"
    },

    // Paths
    paths: {
      title: "Caminhos estruturados para cada religião",
      subtitle: "Cada tradição é apresentada através de módulos claros: fundamentos, textos, ritos, feriados, história e questões contemporâneas.",
      badge: "Caminho",
      islam: "Islamismo",
      islamDesc: "Fundamentos da fé, Alcorão e suras, oração (salat), jejum, feriados, história e diversidade das práticas muçulmanas.",
      islamItem1: "Fundamentos da fé e dos pilares",
      islamItem2: "Estrutura do Alcorão e suras",
      islamItem3: "Oração, jejum, feriados",

    // Settings
    settings: {
      title: "Impostazioni",
      profile: "Profilo",
      username: "Nome utente",
      email: "Email",
      status: "Stato",
      premium: "Premium",
      free: "Gratuito",
      language: "Lingua",
      languageDescription: "Scegli la tua lingua preferita per l'interfaccia",
      notifications: "Notifiche",
      notificationsDescription: "Le impostazioni delle notifiche saranno presto disponibili.",
      privacy: "Privacy",
      privacyPolicy: "Informativa sulla privacy",
      termsOfService: "Termini di servizio",
      backToDashboard: "Torna alla dashboard",
      loading: "Caricamento..."
    },

      islamItem4: "História e diversidade contemporânea",
      christianity: "Cristianismo",
      christianityDesc: "Bíblia e Evangelhos, principais ramos, oração, sacramentos, feriados cristãos, história e questões atuais.",
      christianityItem1: "Bíblia, Novo Testamento, Evangelhos",
      christianityItem2: "Ramos e tradições",
      christianityItem3: "Oração, sacramentos, feriados",
      christianityItem4: "História e mundo contemporâneo",
      judaism: "Judaísmo",
      judaismDesc: "Torá, Tanakh, feriados judaicos, Shabat, práticas comunitárias, história do povo judeu.",
      judaismItem1: "Textos: Torá e Tanakh",
      judaismItem2: "Shabat e vida comunitária",
      judaismItem3: "Feriados e calendário",
      judaismItem4: "Grandes etapas históricas",
      spirituality: "Espiritualidade e diálogo",
      spiritualityDesc: "Valores comuns, ética, compaixão, justiça, interioridade e módulos comparativos.",
      spiritualityItem1: "Valores e ética",
      spiritualityItem2: "Oração e interioridade",
      spiritualityItem3: "Comparações de práticas",
      spiritualityItem4: "Diálogo e compreensão mútua",
      buddhism: "Budismo",
      buddhismDesc: "As 4 Nobres Verdades, o Nobre Caminho Óctuplo, meditação, karma, Nirvana e tradições budistas.",
      buddhismItem1: "As 4 Nobres Verdades",
      buddhismItem2: "O Nobre Caminho Óctuplo",
      buddhismItem3: "Meditação e prática",
      buddhismItem4: "Escolas e tradições",
      hinduism: "Hinduísmo",
      hinduismDesc: "Vedas, Upanishads, karma, dharma, yoga, divindades e práticas espirituais hindus.",
      hinduismItem1: "Textos sagrados e Vedas",
      hinduismItem2: "Karma, dharma e moksha",
      hinduismItem3: "Yoga e meditação",
      hinduismItem4: "Divindades e rituais",
      taoism: "Taoísmo",
      taoismDesc: "O Tao, Wu Wei, Yin-Yang, Zhuangzi, harmonia com a natureza e sabedoria oriental.",
      taoismItem1: "O Tao Te Ching",
      taoismItem2: "Wu Wei e não-ação",
      taoismItem3: "Yin e Yang",
      taoismItem4: "Sabedoria e harmonia",
      transversal: "Transversal",
      transversalDesc: "Percursos comparativos que conectam tradições: não-dualidade, sofrimento, ação justa.",
      transversalItem1: "Não-dualidade e ilusão",
      transversalItem2: "Sofrimento e libertação",
      transversalItem3: "Ação justa",
      transversalItem4: "Pontes entre tradições",
      newBadge: "Novo",
      approach: "Cada caminho é projetado com uma abordagem factual e respeitosa, sem julgamento ou posição doutrinária."
    },

    // Features
    features: {
      title: "Uma pedagogia simples e gamificada",
      subtitle: "O objetivo é tornar o aprendizado sobre religiões tão fluido e motivador quanto aprender um idioma.",
      microLessons: "Micro-lições",
      microLessonsText: "Lições muito curtas para progredir mesmo com uma agenda ocupada.",
      quizzes: "Questionários interativos",
      quizzesText: "Perguntas de múltipla escolha, verdadeiro/falso, associações de conceitos para verificar a compreensão.",
      comparative: "Abordagem comparativa",
      comparativeText: "Módulos que mostram os pontos comuns e as diferenças entre o islamismo, o cristianismo e o judaísmo.",
      gamification: "Gamificação",
      gamificationText: "Pontos de experiência, níveis, sequências de dias consecutivos, distintivos temáticos.",
      progress: "Acompanhamento do progresso",
      progressText: "Painel simples com progresso por caminho, número de lições concluídas.",
      revision: "Modo \"revisão\"",
      revisionText: "Capacidade de repetir rapidamente os questionários para revisar conceitos-chave."
    },

    // FAQ
    faq: {
      title: "Perguntas frequentes",
      subtitle: "Algumas respostas às perguntas mais comuns sobre Spirify.",
      q1: "Spirify promove uma religião?",
      a1: "Não. Spirify é uma plataforma educacional. Ela apresenta as religiões estudadas de maneira factual e respeitosa, sem proselitismo ou julgamento.",
      q2: "Preciso ser crente para usar Spirify?",
      a2: "Não. A plataforma é para crentes e não crentes, curiosos ou em busca de compreensão.",
      q3: "Quem cria o conteúdo?",
      a3: "O conteúdo é desenvolvido a partir de fontes reconhecidas e pode ser revisado por pessoas treinadas em estudos religiosos para garantir uma apresentação equilibrada.",
      q4: "As sete religiões são tratadas igualmente?",
      a4: "Sim. Cada religião (Islã, Cristianismo, Judaísmo, Budismo, Hinduísmo, Taoísmo, Espiritualidade) tem seu próprio caminho estruturado da mesma forma, com o mesmo número de lições e exercícios.",
      q5: "Haverá outras religiões no futuro?",
      a5: "Sim! Enriquecemos continuamente nossa plataforma. Recentemente adicionamos Budismo, Hinduísmo e Taoísmo com 20 lições cada, incluindo níveis Especialistas. Outras religiões e tradições espirituais podem ser adicionadas conforme a demanda.",
      q6: "Como posso ser informado do lançamento?",
      a6: "Você pode criar uma conta gratuita agora mesmo. Você será notificado das novidades por e-mail.",
      q7: "Posso criar perfis para minha família?",
      a7: "Sim! Com uma conta Premium, você pode criar até 4 perfis familiares (1 principal + 3 membros). Cada perfil tem seu próprio progresso, XP e sequência. Perfeito para aprender juntos mantendo uma competição amigável!",
      q8: "Em quais idiomas posso estudar no Spirify?",
      a8: "Spirify está disponível em 6 idiomas: Français, English, Español, Italiano, Português e العربية. Todo o conteúdo (lições, perguntas, explicações) está totalmente traduzido. Você pode mudar de idioma a qualquer momento no app."
    },

    // CTA
    cta: {
      title: "Pronto para começar sua jornada de descoberta?",
      subtitle: "Junte-se a milhares de estudantes que descobrem as religiões de forma lúdica, respeitosa e factual.",
      button: "Começar agora",
      free: "Grátis - Nenhum cartão de crédito necessário"
    },

    // Demo
    demo: {
      live: "AO VIVO",
      play: "Reproduzir",
      pause: "Pausar",
      step1: "Passo 1",
      step2: "Passo 2",
      step3: "Passo 3",
      step4: "Passo 4",
      step5: "Passo 5",
      welcome: "Bem-vindo ao Spirify",
      choosePathSubtitle: "Escolha seu caminho de aprendizado",
      chooseYourPath: "Escolha seu caminho",
      islamChristianismJudaism: "Islã, Cristianismo ou Judaísmo",
      islamCourse: "Curso de Islã",
      fivePillars: "Os Cinco Pilares do Islã",
      startFirstLesson: "Comece sua primeira lição",
      shahada: "Shahada - Profissão de Fé",
      discoverFirstPillar: "Descubra o primeiro pilar do Islã",
      lessons: "lições",
      exercises: "exercícios",
      minutes: "min",
      start: "Começar",
      interactiveQuestion: "Pergunta Interativa",
      testKnowledge: "Teste seus conhecimentos",
      hearts: "corações",
      whatMeansShahada: "O que significa \"Shahada\"?",
      ramadanFasting: "Jejum do Ramadã",
      professionOfFaith: "Profissão de fé islâmica",
      dailyPrayer: "Oração diária",
      pilgrimageToMecca: "Peregrinação a Meca",
      congrats: "Parabéns! Resposta correta",
      correctAnswer: "Excelente resposta! A Shahada é de fato a profissão de fé, o primeiro pilar do Islã.",
      gained: "ganho",
      yourProgress: "Acompanhe seu progresso",
      statsRealTime: "Estatísticas e classificação em tempo real",
      activeStreak: "Sequência ativa",
      days: "dias",
      totalEarned: "Total ganho",
      ranking: "Classificação",
      continueLearning: "Continue aprendendo",
      unlockMore: "Continue para desbloquear mais conteúdo"
    },

    // Footer
    footer: {
      copyright: "© 2024 Spirify – Plataforma de aprendizagem neutra de religiões e espiritualidade.",
      contact: "Contato",
      privacy: "Privacidade",
      legal: "Aviso legal",
      rights: "Todos os direitos reservados."
    },

    // Testimonials
    testimonials: {
      title: "O que dizem sobre Spirify",
      subtitle: "Milhares de usuários descobrem as religiões com Spirify",
      badge: "Depoimentos",
      testimonial1Text: "Uma abordagem neutra e pedagógica que me permitiu compreender melhor o Islã. As lições são curtas e muito bem estruturadas.",
      testimonial1Name: "Marie L.",
      testimonial1Role: "Professora",
      testimonial2Text: "Como muçulmano, apreciei redescobrir minha religião e explorar o cristianismo e o judaísmo. Spirify realmente favorece o diálogo.",
      testimonial2Name: "Ahmed K.",
      testimonial2Role: "Estudante",
      testimonial3Text: "Queria entender as religiões sem me sentir julgada. Spirify é perfeito para isso: neutro, respeitoso e lúdico!",
      testimonial3Name: "Sophie B.",
      testimonial3Role: "Designer",
      testimonial4Text: "Perfeito para minhas aulas de história das religiões. Meus alunos adoram a abordagem lúdica e objetiva do Spirify.",
      testimonial4Name: "Jean P.",
      testimonial4Role: "Professor de história",
      testimonial5Text: "Como judia, pude compreender melhor a história da minha religião e descobrir as outras tradições abraâmicas. Uma verdadeira riqueza!",
      testimonial5Name: "Rachel M.",
      testimonial5Role: "Médica",
      testimonial6Text: "Aplicativo genial para compreender o mundo ao nosso redor. As lições são curtas, perfeitas para minha agenda lotada.",
      testimonial6Name: "Thomas D.",
      testimonial6Role: "Engenheiro",
      stat1: "Usuários ativos",
      stat2: "Lições disponíveis",
      stat3: "Nota média",
      stat4: "Religiões e Espiritualidades"
    },

    // Dashboard
    dashboard: {
      welcome: "Bem-vindo ao Spirify!",
      selectReligion: "Escolha uma religião",
      selectReligionDesc: "Selecione o caminho que deseja começar",
      continue: "Continuar",
      progress: "Progresso",
      lessonsCompleted: "lições concluídas",
      currentStreak: "Sequência atual",
      days: "dias",
      totalXP: "XP Total",
      level: "Nível",
      islam: "Islamismo",
      christianity: "Cristianismo",
      judaism: "Judaísmo",
      spirituality: "Espiritualidade",
      lessons: "lições",
      completed: "concluído",
      locked: "bloqueado",
      start: "Começar",
      resume: "Continuar",
      yourProgress: "Seu progresso",
      xp: "XP",
      hearts: "Corações",
      logout: "Sair",
      loadingLessons: "Carregando lições...",
      noLessons: "Nenhuma lição disponível",
      selectCourse: "Selecione um caminho",
      selectCourseDesc: "Escolha a religião que deseja descobrir",
      startLesson: "Começar",
      unlockAfter: "Desbloquear depois",
      xpEarned: "XP ganho",
      streak: "dias consecutivos",
      todaysGoal: "Objetivo do dia",
      heartsExplanation: "Você perde 1 coração por erro. Mais corações = continue aprendendo!",
      leaderboard: "Classificação",
      gamification: "Progresso",
      badges: "Distintivos",
      goals: "Objetivos",
      settings: "Configurações",
      chooseYourPath: "Escolha seu caminho",
      questionPrompt: "Que religião você gostaria de descobrir hoje?",
      clickToStart: "Clique para começar",
      questions: "Perguntas",
      // Course descriptions
      islamName: "Islamismo",
      islamDesc: "Descubra os fundamentos da fé muçulmana, o Alcorão, as práticas e a história",
      christianismName: "Cristianismo",
      christianismDesc: "Explore a Bíblia, os Evangelhos, os sacramentos e as tradições cristãs",
      judaismName: "Judaísmo",
      judaismDesc: "Compreenda a Torá, as festas judaicas, o Shabat e a história do povo judeu",
      spiritualityName: "Espiritualidade e Diálogo",
      spiritualityDesc: "Descubra os pontos em comum, os valores universais e a abordagem comparativa",
      bouddhismeName: "Budismo",
      bouddhismeDesc: "Descubra os ensinamentos de Buda, a meditação, as Quatro Nobres Verdades e o caminho para a iluminação",
      hindouismeName: "Hinduísmo",
      hindouismeDesc: "Explore os Vedas, o karma, o dharma, as grandes divindades e os caminhos do yoga para a libertação",
      taoismeName: "Taoísmo",
      taoismeDesc: "Mergulhe na sabedoria do Tao, o Wu Wei, a harmonia Yin-Yang e a filosofia de Lao Tzu",
      transversalName: "Transversal",
      transversalDesc: "Percursos comparativos e temáticos que conectam tradições e aprofundam os grandes temas universais"
    },


    // Settings
    settings: {
      title: "Definições",
      profile: "Perfil",
      username: "Nome de usuário",
      email: "Email",
      status: "Estado",
      premium: "Premium",
      free: "Gratuito",
      language: "Idioma",
      languageDescription: "Escolha seu idioma preferido para a interface",
      notifications: "Notificações",
      notificationsDescription: "As configurações de notificação estarão disponíveis em breve.",
      privacy: "Privacidade",
      privacyPolicy: "Política de privacidade",
      termsOfService: "Termos de serviço",
      backToDashboard: "Voltar ao painel",
      loading: "Carregando..."
    },

    // Lesson
    lesson: {
      question: "Pergunta",
      lessonProgress: "Lição",
      loading: "Carregando lição...",
      checkAnswer: "Verificar",
      continue: "Continuar",
      continueLabel: "Continuar",
      nextQuestion: "Próxima pergunta",
      finish: "Concluir lição",
      correct: "Excelente!",
      incorrect: "Não exatamente",
      explanation: "Explicação",
      correctAnswer: "A resposta correta era:",
      heartLost: "Você perdeu 1 coração. Restam",
      heart: "coração",
      hearts: "corações",
      xpGained: "XP ganho",
      reviewMode: "Modo Revisão",
      reviewIntro: "Você cometeu alguns erros. Não se preocupe! Agora você revisará apenas as perguntas que errou.",
      questionsToReview: "perguntas para revisar",
      reviewExplanation: "Responda corretamente para validar a lição!",
      heartsRemaining: "Corações restantes:",
      startReview: "Começar revisão",
      noHearts: "Sem corações!",
      noHeartsMessage: "Você perdeu todos os seus corações. Você deve recomeçar a lição do início.",
      tip: "Dica",
      tipMessage: "Reserve um tempo para ler bem as explicações após cada erro!",
      restart: "Recomeçar lição",
      backToDashboard: "Voltar ao painel",
      correctAnswers: "corretas",
      wellDone: "Parabéns!",
      lessonCompleted: "Lição concluída",
      heartsLeft: "Corações restantes",
      learnMore: "Para saber mais",
      nextLesson: "Próxima lição",
      // Duolingo style micro-challenges
      yourTurn: "Sua vez",
      congrats: "Muito bem!",
      oops: "Ops!",
      tryAgain: "Não está certo, mas você vai aprender!",
      didYouKnow: "Você sabia?",
      theCorrectAnswer: "A resposta correta era:",
      perfectStreak: "Sequência perfeita",
      // Mascot & completion screen messages
      mascotCelebration: "INCRÍVEL!",
      mascotCelebrationSub: "Você é um campeão!",
      mascotHappy: "MUITO BEM!",
      mascotHappySub: "Excelente trabalho!",
      mascotEncouraging: "BOM TRABALHO!",
      mascotEncouragingSub: "Continue assim!",
      mascotSupportive: "BOM ESFORÇO!",
      mascotSupportiveSub: "Você vai conseguir!",
      mascotContinue: "Continuar",
      perfect: "Perfeito!",
      perfectSub: "Você acertou tudo!",
      excellent: "Excelente!",
      excellentSub: "Você realmente dominou este assunto!",
      goodJob: "Bom trabalho!",
      goodJobSub: "Continue assim!",
      notBad: "Nada mal!",
      notBadSub: "Você está progredindo, continue!",
      keepGoing: "Continue!",
      keepGoingSub: "A prática leva à perfeição!",
      accuracy: "Precisão",
      // Combo messages
      combo3: "Pegando fogo!",
      combo5: "Incrível!",
      combo7: "Superstar!",
      combo10: "Lendário!",
      comboPerfect: "Perfeito!",
      comboStreak: "{streak} respostas corretas seguidas!",
      comboPerfectSub: "100% de sucesso!",
      skipReview: "Pular e ver resumo",
      finishReview: "Concluir revisão"
    },

    // Family
    family: {
      title: "Família",
      subtitle: "Aprendam juntos, no seu ritmo.",
      members: "Membros",
      rankings: "Classificação",
      badges: "Badges",
      groupTitle: "Grupo familiar",
      groupSubtitle: "Até 4 perfis na mesma conta.",
      addProfile: "Adicionar perfil",
      createProfile: "Criar perfil",
      inviteFamily: "Convide sua família",
      inviteMessage: "Crie uma competição amigável para aprender mais regularmente.",
      familyRankings: "Classificação familiar",
      rankingsObjective: "Objetivo: progredir juntos, sem pressão.",
      familyBadges: "Badges familiares",
      badgesMessage: "Recompensas pela constância e compromisso.",
      familyStats: "Estatísticas familiares",
      weeklyXP: "XP esta semana",
      monthlyXP: "XP este mês",
      streak: "Sequência",
      week: "Semana",
      month: "Mês",
      pseudo: "Apelido",
      pseudoPlaceholder: "Ex: Mamãe, Papai, Lucas...",
      avatar: "Avatar",
      main: "Principal",
      earned: "Conquistado",
      toUnlock: "A desbloquear",
      totalMembers: "Membros",
      totalXP: "XP acumulados",
      newProfile: "Novo perfil",
      cancel: "Cancelar",
      create: "Criar",
      delete: "Excluir",
      deleteConfirm: "Excluir este perfil? Esta ação é irreversível.",
      profileCreated: "Perfil criado!",
      profileDeleted: "Perfil excluído",
      profilesLabel: "perfis",
      days: "dias",
      daysShort: "d",
      you: "você",
      pseudoRequired: "O apelido é obrigatório",
      active: "Ativo",
      switchedTo: "Perfil ativo",
      whoIsLearning: "Quem está estudando hoje?",
      clickToSwitch: "Clique para ativar este perfil"
    },

    // Leaderboard
    leaderboard: {
      title: "Classificação",
      subtitle: "Progrida a cada dia e suba no ranking.",
      yourRank: "Sua posição",
      topPlayers: "Top aprendizes",
      weekly: "Semana",
      monthly: "Mês",
      allTime: "Geral",
      annualChallenge: "Desafio anual",
      annualDescription: "Todos os anos, os 3 primeiros do ranking global são homenageados.",
      close: "Fechar"
    },

    // Pricing
    pricing: {
      title: "Aprenda as religiões do mundo, jogue em família e pratique idiomas.",
      subtitle: "Uma experiência lúdica, cultural e multilíngue para todas as idades.",
      badge: "Preços simples e transparentes",
      free: "Gratuito",
      premium: "Premium",
      monthly: "Mensal",
      annual: "Anual",
      perMonth: "por mês",
      perYear: "por ano",
      forever: "para sempre",
      subscribe: "Inscrever-se",
      features: "Funcionalidades",
      mostPopular: "Mais popular",
      startFree: "Começar grátis",
      goPremium: "Desbloquear a experiência completa",
      savePercent: "Economize 33%",
      annualPrice: "39,99€/ano",
      freeDescription: "Descoberta completa para começar.",
      lessonsCount: "24 lições (6 por curso)",
      interactiveQuestions: "Perguntas interativas",
      basicGamification: "Gamificação básica",
      heartsPerDay: "5 corações por dia",
      publicLeaderboard: "Classificação pública",
      premiumDescription: "Desbloqueie a experiência completa: aprendizado + idiomas + competição.",
      premiumSubtitle: "Até 4 perfis incluídos — ideal para famílias.",
      everythingFree: "Tudo do plano Gratuito",
      familyMode: "Modo Família (até 4 perfis)",
      allLanguages: "Acesso a todos os idiomas",
      unlimitedAccess: "Acesso ilimitado a todos os cursos",
      annualChallenges: "Rankings e desafios anuais",
      unlimitedHearts: "Corações ilimitados",
      downloadCertificates: "Certificados para download",
      offlineMode: "Modo offline",
      prioritySupport: "Suporte prioritário",
      familyTip: "Dica: lance uma competição amigável em família com o ranking interno (semana / mês / sequência).",
      lockedFamilyMode: "Modo Família (até 4 perfis)",
      lockedAllLanguages: "Acesso a todos os idiomas",
      lockedChallenges: "Desafios anuais e exclusivos",
      lockedAdvancedLevels: "Níveis avançados",
      lockedOfflineMode: "Modo offline",
      footerNote: "Spirify é uma experiência lúdica e respeitosa. Sem proselitismo.",
      ctaTitle: "Pronto para começar sua jornada espiritual?",
      ctaSubtitle: "Junte-se a milhares de alunos que descobrem as religiões de forma divertida e respeitosa.",
      noCreditCard: "Não é necessário cartão de crédito",
      redirecting: "Redirecionando para o pagamento...",
      login: "Entrar"
    }
  },

  ar: {
    auth: {
      login: "تسجيل الدخول",
      signup: "إنشاء حساب",
      email: "البريد الإلكتروني",
      password: "كلمة المرور",
      username: "اسم المستخدم",
      forgotPassword: "نسيت كلمة المرور؟",
      continueWithGoogle: "المتابعة مع جوجل",
      noAccount: "ليس لديك حساب بعد؟",
      haveAccount: "لديك حساب بالفعل؟",
      loginButton: "دخول",
      signupButton: "تسجيل",
      or: "أو",
      welcomeBack: "مرحبًا بعودتك!",
      loginSubtitle: "سجّل دخولك لمواصلة التعلم",
      loggingIn: "جارٍ تسجيل الدخول...",
      createAccount: "أنشئ حسابًا مجانًا",
      emailPlaceholder: "انت@مثال.com",
      errorInvalid: "بريد إلكتروني أو كلمة مرور غير صحيحة",
      signupTitle: "ابدأ مغامرتك",
      signupSubtitle: "أنشئ حسابك المجاني واكتشف الأديان",
      confirmPassword: "تأكيد كلمة المرور",
      creating: "جارٍ الإنشاء...",
      errorPasswordLength: "يجب أن تحتوي كلمة المرور على 6 أحرف على الأقل",
      errorPasswordMatch: "كلمات المرور غير متطابقة",
      errorUsernameLength: "يجب أن يحتوي اسم المستخدم على 3 أحرف على الأقل",
      errorSignup: "خطأ أثناء إنشاء الحساب",
      passwordStrength: "قوة كلمة المرور",
      weak: "ضعيف",
      medium: "متوسط",
      strong: "قوي",
      termsPrefix: "بإنشاءك حسابًا، فإنك توافق على",
      termsLink: "شروط الاستخدام",
      alreadyHaveAccount: "هل لديك حساب بالفعل؟",
      backToHome: "← العودة إلى الصفحة الرئيسية"
    },

    // Navigation
    nav: {
      audience: "لمن هذا",
      howItWorks: "كيف يعمل",
      paths: "المسارات",
      faq: "الأسئلة الشائعة",
      pricing: "الأسعار",
      startFree: "ابدأ مجانًا",
      myDashboard: "لوحة التحكم"
    },
    
    // Hero Section
    hero: {
      badge: "طريقة جديدة لتعلم الأديان",
      title: "اكتشف الأديان في",
      titleHighlight: "5 دقائق يوميًا",
      description: "Spirify هي منصة ممتعة ومحترمة لاكتشاف الأديان ومشاركة التعلم مع العائلة وممارسة اللغات من خلال المحتوى الثقافي، دون التبشير.",
      subtitle: "Spirify هي منصة ممتعة ومحترمة لاكتشاف الأديان ومشاركة التعلم مع العائلة وممارسة اللغات من خلال المحتوى الثقافي، دون التبشير.",
      feature1: "دروس صغيرة مدتها 5-10 دقائق",
      feature2: "5+ أديان وروحانيات",
      feature3: "نهج مقارن ومحترم",
      feature4: "اللعب (نقاط الخبرة والمستويات والسلاسل)",
      cta: "ابدأ مجانًا",
      ctaSecondary: "انظر كيف يعمل",
      noCommitment: "بدون التزام، وصول فوري إلى نسخة الاكتشاف."
    },

    // Audience Section
    audience: {
      title: "لمن Spirify؟",
      subtitle: "Spirify مخصص لأي شخص يريد فهم الأديان دون الدخول في مناقشات لاهوتية معقدة. لا تحتاج إلى أن تكون مؤمنًا أو متخصصًا: تحتاج فقط إلى أن تكون فضوليًا.",
      card1Title: "الفضوليون والباحثون",
      card1Text: "تريد فهمًا أفضل للأديان التي نسمع عنها في الأخبار أو في عائلتك أو في المجتمع، دون أن يكون لديك وقت لقراءة مئات الصفحات.",
      card2Title: "المؤمنون الذين يريدون التعمق",
      card2Text: "تمارس بالفعل دينًا لكنك تريد فهم تاريخه ونصوصه وممارساته بشكل أفضل – واكتشاف كيف تعمل التقاليد الأخرى.",
      card3Title: "الطلاب والمعلمون والوسطاء",
      card3Text: "تعمل مع الشباب أو الجماهير المتنوعة أو في سياقات متعددة الثقافات، وتحتاج إلى أداة بسيطة ومحايدة وتعليمية للتحدث عن الدين."
    },

    // How It Works
    howItWorks: {
      title: "كيف يعمل؟",
      subtitle: "التجربة مصممة كلعبة تعليمية: تختار دينًا أو مسارًا شاملاً، ثم تتقدم من خلال فتح المستويات والدروس والشارات.",
      step1Title: "اختر مسارك",
      step1Text: "الإسلام أو المسيحية أو اليهودية أو \"الروحانية والحوار\": اختر المسار الذي يهمك أكثر، أو ادمجهم.",
      step2Title: "اتبع الدروس الصغيرة اليومية",
      step2Text: "كل درس يستغرق ما بين 5 و 10 دقائق، مع مزيج من النصوص البسيطة والاختبارات والأسئلة متعددة الخيارات والأمثلة الملموسة.",
      step3Title: "تقدم وافتح الشارات",
      step3Text: "تكسب نقاط الخبرة (XP)، وتفتح فصولاً جديدة وتتابع تقدمك يومًا بعد يوم.",
      videoBadge: "شاهد في العمل",
      videoTitle: "اكتشف Spirify في دقيقتين",
      videoSubtitle: "تصفح درسًا كاملاً وشاهد كيف يصبح التعلم لعبة آسرة",
      benefit1: "5 دقائق يوميًا",
      benefit1Sub: "تعلم بالسرعة التي تناسبك، بدون ضغط",
      benefit2: "اللعب",
      benefit2Sub: "نقاط الخبرة والشارات والتصنيفات المحفزة",
      benefit3: "تقدم واضح",
      benefit3Sub: "تتبع تقدمك في الوقت الفعلي",
      demoTitle: "اكتشف منصتنا التفاعلية",
      demoSubtitle: "تعلم بالسرعة التي تناسبك مع دروس ممتعة واختبارات تفاعلية وتتبع تقدمك في الوقت الفعلي.",
      startFree: "ابدأ مجانًا"
    },

    // Paths
    paths: {
      title: "مسارات منظمة لكل دين",
      subtitle: "يتم تقديم كل تقليد من خلال وحدات واضحة: الأساسيات والنصوص والطقوس والأعياد والتاريخ والقضايا المعاصرة.",
      badge: "مسار",
      islam: "الإسلام",
      islamDesc: "أساسيات الإيمان، القرآن والسور، الصلاة، الصيام، الأعياد، التاريخ وتنوع الممارسات الإسلامية.",
      islamItem1: "أساسيات الإيمان والأركان",
      islamItem2: "بنية القرآن والسور",
      islamItem3: "الصلاة والصيام والأعياد",
      islamItem4: "التاريخ والتنوع المعاصر",
      christianity: "المسيحية",
      christianityDesc: "الكتاب المقدس والأناجيل، الفروع الرئيسية، الصلاة، الأسرار، الأعياد المسيحية، التاريخ والقضايا الحالية.",
      christianityItem1: "الكتاب المقدس، العهد الجديد، الأناجيل",
      christianityItem2: "الفروع والتقاليد",
      christianityItem3: "الصلاة، الأسرار المقدسة، الأعياد",
      christianityItem4: "التاريخ والعالم المعاصر",
      judaism: "اليهودية",
      judaismDesc: "التوراة، التناخ، الأعياد اليهودية، السبت، الممارسات المجتمعية، تاريخ الشعب اليهودي.",
      judaismItem1: "النصوص: التوراة والتناخ",
      judaismItem2: "السبت والحياة المجتمعية",
      judaismItem3: "الأعياد والتقويم",
      judaismItem4: "المراحل التاريخية الكبرى",
      spirituality: "الروحانية والحوار",
      spiritualityDesc: "القيم المشتركة، الأخلاق، الرحمة، العدالة، الباطنية، والوحدات المقارنة.",
      spiritualityItem1: "القيم والأخلاق",
      spiritualityItem2: "الصلاة والباطنية",
      spiritualityItem3: "مقارنات الممارسات",
      spiritualityItem4: "الحوار والتفاهم المتبادل",
      buddhism: "البوذية",
      buddhismDesc: "الحقائق النبيلة الأربع، الطريق الثماني النبيل، التأمل، الكارما، النيرفانا والتقاليد البوذية.",
      buddhismItem1: "الحقائق النبيلة الأربع",
      buddhismItem2: "الطريق الثماني النبيل",
      buddhismItem3: "التأمل والممارسة",
      buddhismItem4: "المدارس والتقاليد",
      hinduism: "الهندوسية",
      hinduismDesc: "الفيدا، الأوبانيشاد، الكارما، الدارما، اليوغا، الآلهة والممارسات الروحية الهندوسية.",
      hinduismItem1: "النصوص المقدسة والفيدا",
      hinduismItem2: "الكارما والدارما والموكشا",
      hinduismItem3: "اليوغا والتأمل",
      hinduismItem4: "الآلهة والطقوس",
      taoism: "الطاوية",
      taoismDesc: "التاو، وو وي، الين واليانغ، تشوانغ تزو، الانسجام مع الطبيعة والحكمة الشرقية.",
      taoismItem1: "تاو تي تشينغ",
      taoismItem2: "وو وي واللافعل",
      taoismItem3: "الين واليانغ",
      taoismItem4: "الحكمة والانسجام",
      transversal: "عبر التقاليد",
      transversalDesc: "مسارات مقارنة تربط التقاليد: اللاثنائية، المعاناة، الفعل الصحيح.",
      transversalItem1: "اللاثنائية والوهم",
      transversalItem2: "المعاناة والتحرر",
      transversalItem3: "الفعل الصحيح",
      transversalItem4: "جسور بين التقاليد",
      newBadge: "جديد",
      approach: "كل مسار مصمم بنهج واقعي ومحترم، دون أحكام أو مواقف عقائدية."
    },

    // Features
    features: {
      title: "تربية بسيطة ولعبة",
      subtitle: "الهدف هو جعل تعلم الأديان سلسًا ومحفزًا مثل تعلم لغة.",
      microLessons: "دروس صغيرة",
      microLessonsText: "دروس قصيرة جدًا للتقدم حتى مع جدول مزدحم.",
      quizzes: "اختبارات تفاعلية",
      quizzesText: "أسئلة متعددة الخيارات، صواب/خطأ، ارتباطات المفاهيم للتحقق من الفهم.",
      comparative: "نهج مقارن",
      comparativeText: "وحدات تظهر القواسم المشتركة والاختلافات بين الإسلام والمسيحية واليهودية.",
      gamification: "اللعب",
      gamificationText: "نقاط الخبرة والمستويات وسلاسل الأيام المتتالية والشارات الموضوعية.",
      progress: "تتبع التقدم",
      progressText: "لوحة معلومات بسيطة مع التقدم حسب المسار، وعدد الدروس المكتملة.",
      revision: "وضع \"المراجعة\"",
      revisionText: "القدرة على إعادة تشغيل الاختبارات بسرعة لمراجعة المفاهيم الأساسية."
    },

    // FAQ
    faq: {
      title: "الأسئلة الشائعة",
      subtitle: "بعض الإجابات على الأسئلة الأكثر شيوعًا حول Spirify.",
      q1: "هل تروج Spirify لدين؟",
      a1: "لا. Spirify هي منصة تعليمية. تقدم الأديان المدروسة بطريقة واقعية ومحترمة، دون التبشير أو الحكم.",
      q2: "هل أحتاج إلى أن أكون مؤمنًا لاستخدام Spirify؟",
      a2: "لا. المنصة مخصصة للمؤمنين وغير المؤمنين، الفضوليين أو الباحثين عن الفهم.",
      q3: "من يقوم بإنشاء المحتوى؟",
      a3: "يتم تطوير المحتوى من مصادر معترف بها ويمكن مراجعته من قبل أشخاص مدربين في الدراسات الدينية لضمان عرض متوازن.",
      q4: "هل تعامل الأديان السبعة بالتساوي؟",
      a4: "نعم. كل دين (الإسلام، المسيحية، اليهودية، البوذية، الهندوسية، الطاوية، الروحانية) له مساره الخاص المنظم بنفس الطريقة، مع نفس عدد الدروس والتمارين.",
      q5: "هل ستكون هناك أديان أخرى في المستقبل؟",
      a5: "نعم! نحن نثري منصتنا باستمرار. أضفنا مؤخرًا البوذية والهندوسية والطاوية مع 20 درسًا لكل منها، بما في ذلك مستويات الخبراء. يمكن إضافة أديان وتقاليد روحية أخرى حسب الطلب.",
      q6: "كيف يمكنني أن أكون على علم بالإطلاق؟",
      a6: "يمكنك إنشاء حساب مجاني الآن. سيتم إخطارك بالميزات الجديدة عبر البريد الإلكتروني.",
      q7: "هل يمكنني إنشاء ملفات شخصية لعائلتي؟",
      a7: "نعم! مع حساب بريميوم، يمكنك إنشاء ما يصل إلى 4 ملفات شخصية عائلية (1 رئيسي + 3 أعضاء). كل ملف شخصي له تقدمه الخاص و XP وسلسلته. مثالي للتعلم معًا مع الحفاظ على منافسة ودية!",
      q8: "بأي لغات يمكنني الدراسة على Spirify؟",
      a8: "Spirify متاح بـ 6 لغات: Français و English و Español و Italiano و Português و العربية. كل المحتوى (الدروس والأسئلة والتفسيرات) مترجم بالكامل. يمكنك تغيير اللغة في أي وقت في التطبيق."
    },

    // CTA
    cta: {
      title: "هل أنت مستعد لبدء رحلة الاكتشاف الخاصة بك؟",
      subtitle: "انضم إلى آلاف الطلاب الذين يكتشفون الأديان بطريقة ممتعة ومحترمة وواقعية.",
      button: "ابدأ الآن",
      free: "مجانًا - لا حاجة لبطاقة ائتمان"
    },

    // Demo
    demo: {
      live: "مباشر",
      play: "تشغيل",
      pause: "إيقاف",
      step1: "الخطوة 1",
      step2: "الخطوة 2",
      step3: "الخطوة 3",
      step4: "الخطوة 4",
      step5: "الخطوة 5",
      welcome: "مرحبًا بك في Spirify",
      choosePathSubtitle: "اختر مسار التعلم الخاص بك",
      chooseYourPath: "اختر مسارك",
      islamChristianismJudaism: "الإسلام أو المسيحية أو اليهودية",
      islamCourse: "مسار الإسلام",
      fivePillars: "أركان الإسلام الخمسة",
      startFirstLesson: "ابدأ درسك الأول",
      shahada: "الشهادة - شهادة الإيمان",
      discoverFirstPillar: "اكتشف الركن الأول من أركان الإسلام",
      lessons: "دروس",
      exercises: "تمارين",
      minutes: "دقيقة",
      start: "ابدأ",
      interactiveQuestion: "سؤال تفاعلي",
      testKnowledge: "اختبر معرفتك",
      hearts: "قلوب",
      whatMeansShahada: "ماذا تعني \"الشهادة\"؟",
      ramadanFasting: "صيام رمضان",
      professionOfFaith: "شهادة الإيمان الإسلامية",
      dailyPrayer: "الصلاة اليومية",
      pilgrimageToMecca: "الحج إلى مكة",
      congrats: "أحسنت! إجابة صحيحة",
      correctAnswer: "إجابة ممتازة! الشهادة هي بالفعل شهادة الإيمان، الركن الأول من أركان الإسلام.",
      gained: "مكتسب",
      yourProgress: "تابع تقدمك",
      statsRealTime: "الإحصائيات والترتيب في الوقت الفعلي",
      activeStreak: "سلسلة نشطة",
      days: "يوم",
      totalEarned: "إجمالي المكتسب",
      ranking: "الترتيب",
      continueLearning: "استمر في التعلم",
      unlockMore: "استمر لفتح المزيد من المحتوى"
    },

    // Footer
    footer: {
      copyright: "© 2024 Spirify – منصة تعليمية محايدة للأديان والروحانية.",
      contact: "اتصل",
      privacy: "الخصوصية",
      legal: "إشعار قانوني",
      rights: "جميع الحقوق محفوظة."
    },

    // Testimonials
    testimonials: {
      title: "ماذا يقولون عن Spirify",
      subtitle: "آلاف المستخدمين يكتشفون الأديان مع Spirify",
      badge: "شهادات",
      testimonial1Text: "نهج محايد وتربوي سمح لي بفهم الإسلام بشكل أفضل. الدروس قصيرة ومنظمة بشكل جيد للغاية.",
      testimonial1Name: "Marie L.",
      testimonial1Role: "معلمة",
      testimonial2Text: "كمسلم، قدّرت إعادة اكتشاف ديني واستكشاف المسيحية واليهودية. Spirify يعزز حقًا الحوار.",
      testimonial2Name: "Ahmed K.",
      testimonial2Role: "طالب",
      testimonial3Text: "أردت فهم الأديان دون أن أشعر بالحكم. Spirify مثالي لذلك: محايد ومحترم وممتع!",
      testimonial3Name: "Sophie B.",
      testimonial3Role: "مصممة",
      testimonial4Text: "مثالي لدروس تاريخ الأديان الخاصة بي. طلابي يحبون النهج الممتع والموضوعي لـ Spirify.",
      testimonial4Name: "Jean P.",
      testimonial4Role: "أستاذ تاريخ",
      testimonial5Text: "كيهودية، تمكنت من فهم تاريخ ديني بشكل أفضل واكتشاف التقاليد الإبراهيمية الأخرى. ثروة حقيقية!",
      testimonial5Name: "Rachel M.",
      testimonial5Role: "طبيبة",
      testimonial6Text: "تطبيق رائع لفهم العالم من حولنا. الدروس قصيرة، مثالية لجدولي المزدحم.",
      testimonial6Name: "Thomas D.",
      testimonial6Role: "مهندس",
      stat1: "المستخدمون النشطون",
      stat2: "الدروس المتاحة",
      stat3: "التقييم المتوسط",
      stat4: "الأديان والروحانيات"
    },

    // Dashboard
    dashboard: {
      welcome: "مرحبًا بك في Spirify!",
      selectReligion: "اختر دينًا",
      selectReligionDesc: "اختر المسار الذي تريد البدء به",
      continue: "متابعة",
      progress: "التقدم",
      lessonsCompleted: "الدروس المكتملة",
      currentStreak: "السلسلة الحالية",
      days: "أيام",
      totalXP: "إجمالي XP",
      level: "المستوى",
      islam: "الإسلام",
      christianity: "المسيحية",
      judaism: "اليهودية",
      spirituality: "الروحانية",
      lessons: "دروس",
      completed: "مكتمل",
      locked: "مقفل",
      start: "ابدأ",
      resume: "استئناف",
      yourProgress: "تقدمك",
      xp: "XP",
      hearts: "قلوب",
      logout: "تسجيل خروج",
      loadingLessons: "جارٍ تحميل الدروس...",
      noLessons: "لا توجد دروس متاحة",
      selectCourse: "اختر مسارًا",
      selectCourseDesc: "اختر الدين الذي تريد اكتشافه",
      startLesson: "ابدأ",
      unlockAfter: "إلغاء القفل بعد",
      xpEarned: "XP مكتسب",
      streak: "أيام متتالية",
      todaysGoal: "هدف اليوم",
      heartsExplanation: "تخسر قلبًا واحدًا لكل خطأ. المزيد من القلوب = استمر في التعلم!",
      leaderboard: "التصنيف",
      gamification: "التقدم",
      badges: "الشارات",
      goals: "الأهداف",
      settings: "الإعدادات",
      chooseYourPath: "اختر مسارك",
      continue: "متابعة",
      welcome: "مرحبا",
      questionPrompt: "ما الدين الذي تود اكتشافه اليوم؟",
      clickToStart: "انقر للبدء",
      questions: "أسئلة",
      // Course descriptions
      islamName: "الإسلام",
      islamDesc: "اكتشف أسس العقيدة الإسلامية والقرآن والممارسات والتاريخ",
      christianismName: "المسيحية",
      christianismDesc: "استكشف الكتاب المقدس والأناجيل والأسرار المقدسة والتقاليد المسيحية",
      judaismName: "اليهودية",
      judaismDesc: "افهم التوراة والأعياد اليهودية والسبت وتاريخ الشعب اليهودي",
      spiritualityName: "الروحانية والحوار",
      spiritualityDesc: "اكتشف القواسم المشتركة والقيم العالمية والنهج المقارن",
      bouddhismeName: "البوذية",
      bouddhismeDesc: "اكتشف تعاليم بوذا والتأمل والحقائق النبيلة الأربع والطريق نحو التنوير",
      hindouismeName: "الهندوسية",
      hindouismeDesc: "استكشف الفيدا والكارما والدارما والآلهة الكبرى وطرق اليوغا نحو التحرر",
      taoismeName: "الطاوية",
      taoismeDesc: "انغمس في حكمة الطاو والوو وي وانسجام الين واليانغ وفلسفة لاو تزو",
      transversalName: "عبر التقاليد",
      transversalDesc: "مسارات مقارنة وموضوعية تربط بين التقاليد وتتعمق في الموضوعات الكونية الكبرى"
    },

    // Lesson
    lesson: {
      question: "سؤال",
      lessonProgress: "درس",
      loading: "جارٍ تحميل الدرس...",
      checkAnswer: "تحقق",
      continue: "متابعة",
      nextQuestion: "السؤال التالي",
      finish: "إنهاء الدرس",
      correct: "ممتاز!",
      incorrect: "ليس تمامًا",
      explanation: "شرح",
      correctAnswer: "الإجابة الصحيحة كانت:",
      heartLost: "لقد خسرت قلبًا واحدًا. يتبقى لديك",
      heart: "قلب",
      hearts: "قلوب",
      xpGained: "XP مكتسب",
      reviewMode: "وضع المراجعة",
      reviewIntro: "لقد ارتكبت بعض الأخطاء. لا تقلق! ستقوم الآن بمراجعة الأسئلة التي أخطأت فيها فقط.",
      questionsToReview: "أسئلة للمراجعة",
      reviewExplanation: "أجب بشكل صحيح للتحقق من الدرس!",
      heartsRemaining: "القلوب المتبقية:",
      startReview: "ابدأ المراجعة",
      noHearts: "لا توجد قلوب!",
      noHeartsMessage: "لقد خسرت جميع قلوبك. يجب أن تعيد بدء الدرس من البداية.",
      tip: "نصيحة",
      tipMessage: "خذ وقتك لقراءة الشروحات جيدًا بعد كل خطأ!",
      restart: "إعادة بدء الدرس",
      correctAnswers: "صحيحة",
      wellDone: "أحسنت!",
      lessonCompleted: "اكتمل الدرس",
      heartsLeft: "القلوب المتبقية",
      learnMore: "لمعرفة المزيد",
      nextLesson: "الدرس التالي",
      backToDashboard: "العودة إلى لوحة التحكم",
      // Mascot & completion screen messages
      mascotCelebration: "!مذهل",
      mascotCelebrationSub: "!أنت بطل",
      mascotHappy: "!أحسنت",
      mascotHappySub: "!عمل ممتاز",
      mascotEncouraging: "!عمل جيد",
      mascotEncouragingSub: "!استمر هكذا",
      mascotSupportive: "!جهد جيد",
      mascotSupportiveSub: "!ستنجح",
      mascotContinue: "متابعة",
      perfect: "!مثالي",
      perfectSub: "!أجبت على كل شيء بشكل صحيح",
      excellent: "!ممتاز",
      excellentSub: "!أنت تتقن هذا الموضوع حقًا",
      goodJob: "!عمل جيد",
      goodJobSub: "!استمر هكذا",
      notBad: "!ليس سيئًا",
      notBadSub: "!أنت تتقدم، استمر",
      keepGoing: "!استمر",
      keepGoingSub: "!الممارسة تصنع الكمال",
      accuracy: "الدقة",
      // Combo messages
      combo3: "!مشتعل",
      combo5: "!لا يصدق",
      combo7: "!نجم",
      combo10: "!أسطوري",
      comboPerfect: "!مثالي",
      comboStreak: "!إجابات صحيحة متتالية {streak}",
      comboPerfectSub: "!نجاح 100%",
      skipReview: "تخطي وعرض الملخص",
      finishReview: "إنهاء المراجعة",
      // Duolingo style micro-challenges
      yourTurn: "دورك",
      congrats: "أحسنت!",
      oops: "عفوًا!",
      tryAgain: "ليست الإجابة الصحيحة، لكنك ستتعلم!",
      didYouKnow: "هل كنت تعلم؟",
      theCorrectAnswer: "الإجابة الصحيحة كانت:",
      perfectStreak: "سلسلة مثالية",
      continueLabel: "متابعة"
    },

    // Family
    family: {
      title: "العائلة",
      subtitle: "تعلموا معًا، بالإيقاع الذي يناسبكم.",
      members: "الأعضاء",
      rankings: "التصنيف",
      badges: "الشارات",
      groupTitle: "مجموعة العائلة",
      groupSubtitle: "حتى 4 ملفات شخصية في نفس الحساب.",
      addProfile: "إضافة ملف شخصي",
      createProfile: "إنشاء ملف شخصي",
      inviteFamily: "ادعُ عائلتك",
      inviteMessage: "أنشئ منافسة ودية للتعلم بانتظام أكثر.",
      familyRankings: "تصنيف العائلة",
      rankingsObjective: "الهدف: التقدم معًا، بدون ضغط.",
      familyBadges: "شارات العائلة",
      badgesMessage: "مكافآت على الثبات والالتزام.",
      familyStats: "إحصائيات العائلة",
      weeklyXP: "XP هذا الأسبوع",
      monthlyXP: "XP هذا الشهر",
      streak: "السلسلة",
      week: "أسبوع",
      month: "شهر",
      pseudo: "اللقب",
      pseudoPlaceholder: "مثال: ماما، بابا، لوكاس...",
      avatar: "الصورة الرمزية",
      main: "الرئيسي",
      earned: "تم الحصول عليه",
      toUnlock: "للفتح",
      totalMembers: "الأعضاء",
      totalXP: "XP المتراكم",
      newProfile: "ملف شخصي جديد",
      cancel: "إلغاء",
      create: "إنشاء",
      delete: "حذف",
      deleteConfirm: "حذف هذا الملف الشخصي؟ هذا الإجراء لا رجعة فيه.",
      profileCreated: "!تم إنشاء الملف الشخصي",
      profileDeleted: "تم حذف الملف الشخصي",
      profilesLabel: "ملفات شخصية",
      days: "أيام",
      daysShort: "ي",
      you: "أنت",
      pseudoRequired: "اللقب مطلوب",
      active: "نشط",
      switchedTo: "الملف الشخصي النشط",
      whoIsLearning: "من يتعلم اليوم؟",
      clickToSwitch: "انقر لتفعيل هذا الملف الشخصي"
    },

    // Leaderboard
    leaderboard: {
      title: "التصنيف",
      subtitle: "تقدم كل يوم وارتقِ في التصنيف.",
      yourRank: "مركزك",
      topPlayers: "أفضل المتعلمين",
      weekly: "أسبوع",
      monthly: "شهر",
      allTime: "الإجمالي",
      annualChallenge: "التحدي السنوي",
      annualDescription: "كل عام، يتم تكريم أفضل 3 في التصنيف العام.",
      close: "إغلاق"
    },

    // Settings
    settings: {
      title: "الإعدادات",
      profile: "الملف الشخصي",
      username: "اسم المستخدم",
      email: "البريد الإلكتروني",
      status: "الحالة",
      premium: "بريميوم",
      free: "مجاني",
      language: "اللغة",
      languageDescription: "اختر لغتك المفضلة للواجهة",
      notifications: "الإشعارات",
      notificationsDescription: "ستكون إعدادات الإشعارات متاحة قريبًا.",
      privacy: "الخصوصية",
      privacyPolicy: "سياسة الخصوصية",
      termsOfService: "شروط الخدمة",
      backToDashboard: "العودة إلى لوحة التحكم",
      loading: "جارٍ التحميل..."
    },

    // Pricing
    pricing: {
      title: "تعلّم أديان العالم، العب مع العائلة، ومارس اللغات.",
      subtitle: "تجربة ممتعة وثقافية ومتعددة اللغات لجميع الأعمار.",
      badge: "أسعار بسيطة وشفافة",
      free: "مجاني",
      premium: "بريميوم",
      monthly: "شهري",
      annual: "سنوي",
      perMonth: "شهريًا",
      perYear: "سنويًا",
      forever: "للأبد",
      subscribe: "اشترك",
      features: "المزايا",
      mostPopular: "الأكثر شعبية",
      startFree: "ابدأ مجانًا",
      goPremium: "افتح التجربة الكاملة",
      savePercent: "وفّر 33%",
      annualPrice: "39.99€/سنة",
      freeDescription: "اكتشاف كامل للبدء.",
      lessonsCount: "24 درسًا (6 لكل دورة)",
      interactiveQuestions: "أسئلة تفاعلية",
      basicGamification: "ألعاب أساسية",
      heartsPerDay: "5 قلوب يوميًا",
      publicLeaderboard: "لوحة المتصدرين العامة",
      premiumDescription: "افتح التجربة الكاملة: تعلم + لغات + منافسة.",
      premiumSubtitle: "حتى 4 ملفات شخصية مضمنة — مثالي للعائلات.",
      everythingFree: "كل شيء في الخطة المجانية",
      familyMode: "وضع العائلة (حتى 4 ملفات)",
      allLanguages: "الوصول إلى جميع اللغات",
      unlimitedAccess: "وصول غير محدود لجميع الدورات",
      annualChallenges: "التصنيفات والتحديات السنوية",
      unlimitedHearts: "قلوب غير محدودة",
      downloadCertificates: "شهادات قابلة للتحميل",
      offlineMode: "وضع عدم الاتصال",
      prioritySupport: "دعم ذو أولوية",
      familyTip: "نصيحة: ابدأ منافسة ودية في العائلة مع التصنيف الداخلي (أسبوع / شهر / سلسلة).",
      lockedFamilyMode: "وضع العائلة (حتى 4 ملفات)",
      lockedAllLanguages: "الوصول إلى جميع اللغات",
      lockedChallenges: "تحديات سنوية وحصرية",
      lockedAdvancedLevels: "المستويات المتقدمة",
      lockedOfflineMode: "وضع عدم الاتصال",
      footerNote: "سبيريفاي تجربة ممتعة ومحترمة. لا تبشير.",
      ctaTitle: "مستعد لبدء رحلتك الروحية؟",
      ctaSubtitle: "انضم إلى آلاف المتعلمين الذين يكتشفون الأديان بطريقة ممتعة ومحترمة.",
      noCreditCard: "لا حاجة لبطاقة ائتمان",
      redirecting: "جارٍ التحويل إلى الدفع...",
      login: "تسجيل الدخول"
    },

    leaderboardPage: {
      loading: "جارٍ تحميل التصنيف...",
      yourRank: "ترتيبك",
      outOf: "من أصل",
      learners: "متعلم",
      days: "يوم",
      top3: "أفضل 3",
      ranking: "التصنيف العام",
      otherRanked: "المرتبون الآخرون",
      level: "المستوى"
    },

    contact: {
      title: "اتصل بنا",
      subtitle: "هل لديك سؤال؟ مشكلة؟ لا تتردد في الكتابة إلينا.",
      name: "الاسم",
      namePlaceholder: "اسمك",
      email: "البريد الإلكتروني",
      emailPlaceholder: "بريدك@email.com",
      subject: "الموضوع",
      subjectPlaceholder: "موضوع رسالتك",
      message: "الرسالة",
      messagePlaceholder: "رسالتك...",
      send: "إرسال",
      sending: "جارٍ الإرسال...",
      thankYou: "شكرًا لك!",
      messageSent: "تم إرسال رسالتك بنجاح. سنرد عليك في أقرب وقت ممكن.",
      backToDashboard: "العودة إلى لوحة التحكم"
    },

    privacy: {
      title: "سياسة الخصوصية",
      lastUpdate: "آخر تحديث",
      intro: "في Spirify، نولي أهمية كبيرة لحماية بياناتك الشخصية. توضح سياسة الخصوصية هذه المعلومات التي نجمعها وكيفية استخدامها وما هي حقوقك.",
      dataCollected: "البيانات المجمعة",
      dataCollectedText: "نجمع البيانات التالية:",
      data1: "معلومات الحساب: اسم المستخدم، عنوان البريد الإلكتروني",
      data2: "بيانات التقدم: الدروس المكتملة، النتائج، XP",
      data3: "البيانات التقنية: نوع المتصفح، عنوان IP (مجهول الهوية)",
      dataUsage: "استخدام البيانات",
      dataUsageText: "تُستخدم بياناتك من أجل:",
      usage1: "تقديم وتحسين خدماتنا",
      usage2: "تخصيص تجربة التعلم الخاصة بك",
      usage3: "إرسال إشعارات مهمة إليك",
      usage4: "إعداد إحصائيات مجهولة الهوية",
      dataProtection: "حماية البيانات",
      dataProtectionText: "نطبق إجراءات أمنية تقنية وتنظيمية مناسبة لحماية بياناتك من الوصول غير المصرح به أو التعديل أو الإفشاء أو التدمير. يتم تخزين بياناتك على خوادم آمنة ومشفرة.",
      yourRights: "حقوقك",
      yourRightsText: "وفقًا لـ GDPR، لديك الحقوق التالية:",
      right1: "حق الوصول إلى بياناتك",
      right2: "حق التصحيح",
      right3: "حق المحو (الحق في النسيان)",
      right4: "حق نقل البيانات",
      right5: "حق الاعتراض على المعالجة",
      cookies: "ملفات تعريف الارتباط",
      cookiesText: "نستخدم ملفات تعريف الارتباط الأساسية لعمل الموقع (المصادقة، تفضيلات اللغة). لا يتم استخدام أي ملفات تعريف ارتباط إعلانية أو تتبع.",
      contact: "اتصل بنا",
      contactText: "لممارسة حقوقك أو لأي أسئلة تتعلق ببياناتك الشخصية، اتصل بنا:",
      back: "رجوع"
    },

    legal: {
      title: "الإشعارات القانونية",
      editor: "ناشر الموقع",
      hosting: "الاستضافة",
      intellectualProperty: "الملكية الفكرية",
      intellectualPropertyText: "يخضع هذا الموقع بأكمله للتشريعات الفرنسية والدولية المتعلقة بحقوق النشر والملكية الفكرية. جميع حقوق النسخ محفوظة، بما في ذلك المستندات القابلة للتحميل والتمثيلات الأيقونية والفوتوغرافية.",
      personalData: "البيانات الشخصية",
      personalDataText: "وفقًا للائحة العامة لحماية البيانات (GDPR)، لديك الحق في الوصول إلى البيانات المتعلقة بك وتصحيحها وحذفها. لممارسة هذا الحق، اتصل بنا على: contact@spirify.ai",
      cookies: "ملفات تعريف الارتباط",
      cookiesText: "يستخدم هذا الموقع ملفات تعريف الارتباط لتحسين تجربة المستخدم وإجراء إحصائيات الزيارات. من خلال الاستمرار في تصفح هذا الموقع، فإنك توافق على استخدام ملفات تعريف الارتباط.",
      liability: "المسؤولية",
      liabilityText: "المعلومات المقدمة على هذا الموقع هي لأغراض إعلامية فقط. لا يمكن لـ MFP Services France ضمان دقة أو اكتمال أو حداثة المعلومات المنشورة على الموقع.",
      lastUpdate: "آخر تحديث"
    }
  }
};

export default translations;
